import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PlaceIcon from '@mui/icons-material/Place';
import RecyclingIcon from '@mui/icons-material/Recycling';
import {
    Typography,
    Grid,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    IconButton,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Snackbar,
    Avatar,
    Switch,
    FormControlLabel,
    CircularProgress,
    TableRow,
    TableCell,
    Autocomplete,
    Paper,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { collection, getDocs, doc, updateDoc, writeBatch, increment, getDoc, deleteDoc, setDoc, addDoc, arrayRemove, query, Timestamp, where, limit, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { RouteGroup, Route, Client, Order, InventoryItem, PedidoPreprogramado, Transportista } from '../models';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import { ChevronLeft, ChevronRight, Combine } from 'lucide-react';
import OrderCreationModal from './OrderCreationModal';
import { toast } from 'react-toastify';
import { styled, Theme } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { keyframes } from '@mui/system';
import { optimizeWaypoints } from '../utils/routeOptimization'; // You'll need to create this utility function
import { LocalShipping, Store, PendingActions, CheckCircle, Cancel, AttachMoney as AttachMoneyIcon, Info as InfoIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { SvgIcon, SvgIconProps, useMediaQuery, useTheme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MapIcon from '@mui/icons-material/DirectionsCar';
import WazeIcon from '@mui/icons-material/Navigation'; // Usamos DirectionsCar como sustituto de Waze
import "react-datepicker/dist/react-datepicker.css";
import { CalendarToday, Repeat, Today } from '@mui/icons-material';
import { FormatListBulleted } from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import { el, es, ro } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'; // Importar el icono del calendario
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import firebase from 'firebase/app'; // Adjust the import based on your Firebase version
import GoogleIcon from '@mui/icons-material/Google';
import { FaWaze } from 'react-icons/fa';
import OrderCreationModalAutomatic from './OrderCreationModalAutomatic';

interface OrdersCalendarState {
    editOrder: { routeGroupId: string; routeUnionId: string; route: Route } | null;
}

// Ensure marker icons are displayed correctly
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});


const generateDates = (createdAtDay: Date, oneMonthFromNow: Date, routeType: string): Date[] => {
    const dates: Date[] = [];
    let currentIterationDate = new Date(createdAtDay);

    while (currentIterationDate <= oneMonthFromNow) {
        if (routeType === 'weekly') {
            dates.push(new Date(currentIterationDate));
            currentIterationDate.setDate(currentIterationDate.getDate() + 7);
        } else if (routeType === 'biweekly') {
            dates.push(new Date(currentIterationDate));
            currentIterationDate.setDate(currentIterationDate.getDate() + 14);
        }
    }

    return dates;
};

const generateMonthlyDates = (dayOfWeek: number, weekOfMonth: number, oneMonthFromNow: Date, routeType: string): Date[] => {
    const dates: Date[] = [];
    let currentIterationDate = new Date();

    const currentDayOfWeek = currentIterationDate.getDay();
    const currentDay = currentIterationDate.getDate();
    const currentMonth = currentIterationDate.getMonth();
    const currentYear = currentIterationDate.getFullYear();
    const currentWeekOfMonth = Math.ceil(currentDay / 7);

    if (currentWeekOfMonth > weekOfMonth || (currentWeekOfMonth === weekOfMonth && currentDayOfWeek > dayOfWeek)) {
        currentIterationDate = new Date(currentYear, currentMonth + 1, 1);
    } else {
        currentIterationDate = new Date(currentYear, currentMonth, 1);
    }

    currentIterationDate.setDate((weekOfMonth - 1) * 7 + 1);
    while (currentIterationDate.getDay() !== dayOfWeek) {
        currentIterationDate.setDate(currentIterationDate.getDate() + 1);
    }

    while (currentIterationDate <= oneMonthFromNow) {
        dates.push(new Date(currentIterationDate));
        if (routeType === 'monthly') {
            currentIterationDate.setMonth(currentIterationDate.getMonth() + 1);
        } else if (routeType === 'bimonthly') {
            currentIterationDate.setMonth(currentIterationDate.getMonth() + 2);
        } else {
            currentIterationDate.setDate(currentIterationDate.getDate() + 1);
        }
    }

    return dates;
};

const MarkerIcon: React.FC<{ number: string }> = ({ number }) => {
    return (
        <div style={{
            color: 'white',
            fontWeight: 'bold',
            textAlign: 'center',
            lineHeight: '20px', // Ajusta según el tamaño del círculo
        }}>
            {number}
        </div>
    );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        overflow: 'hidden',
    },
}));

const DialogTitleWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#87CEEB',
    color: 'white',
    padding: theme.spacing(2),
    position: 'relative',
}));

const DateCircle = styled(Box)(({ theme }) => ({
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: 'white',
    color: '#87CEEB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#00b4d8',
        },
        '&:hover fieldset': {
            borderColor: '#0095b6',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#00b4d8',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#00b4d8',
    },
}));

// Componente separado para el input del nombre
const NameInput = React.memo(({ initialName, onNameChange }: { initialName: string, onNameChange: (name: string) => void }) => {
    const [localName, setLocalName] = useState(initialName);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalName(e.target.value);
    };

    const handleBlur = () => {
        onNameChange(localName);
    };

    return (
        <StyledFormControl fullWidth>
            <TextField
                fullWidth
                label="Nombre"
                value={localName}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </StyledFormControl>
    );
});


const RouteTable = styled(Box)(({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: theme.spacing(2),
}));

const RouteRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: '4px',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    '&:hover': {
        boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
    },
}));

const OrderNumber = styled(Box)(({ theme }) => ({
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: '#FFA500',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    color: 'white',
    fontWeight: 'bold',
}));

const RouteTableHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px 4px 0 0',
    padding: theme.spacing(2),
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
}));

const HeaderCell = styled(Box)(({ theme }) => ({
    flexGrow: 0,
    flexShrink: 0,
    marginRight: theme.spacing(2),
}));

const columnNames: Record<string, string> = {
    cant_gato: 'Gatos',
    cant_perro_mini: 'Perros Mini',
    cant_perro_pequeno: 'Perros Pequeños',
    cant_perro_mediano: 'Perros Medianos',
    cant_perro_grande: 'Perros Grandes',
    cant_perro_gigante: 'Perros Gigantes'
};

// Definir los iconos personalizados
const OllinIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon {...props}>
        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2" />
    </SvgIcon>
);

const RecoleccionIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon {...props} viewBox="0 0 512 512">
        <path d="M184.561 261.903c3.232 13.997-12.123 24.635-24.068 17.168l-40.736-25.455-50.867 81.402C55.606 356.273 70.96 384 96.012 384H148c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12H96.115c-75.334 0-121.302-83.048-81.408-146.88l50.822-81.388-40.725-25.448c-12.081-7.547-8.966-25.961 4.879-29.158l110.237-25.45c8.611-1.988 17.201 3.381 19.189 11.99l25.452 110.237zm98.561-182.915l41.289 66.076-40.74 25.457c-12.051 7.528-9 25.953 4.879 29.158l110.237 25.45c8.672 1.999 17.215-3.438 19.189-11.99l25.45-110.237c3.197-13.844-11.99-24.719-24.068-17.168l-40.687 25.424-41.263-66.082c-37.521-60.033-125.209-60.171-162.816 0l-17.963 28.766c-3.51 5.62-1.8 13.021 3.82 16.533l33.919 21.195c5.62 3.512 13.024 1.803 16.536-3.817l17.961-28.743c12.712-20.341 41.973-19.676 54.257-.022zM497.288 301.12l-27.515-44.065c-3.511-5.623-10.916-7.334-16.538-3.821l-33.861 21.159c-5.62 3.512-7.33 10.915-3.818 16.536l27.564 44.112c13.257 21.211-2.057 48.96-27.136 48.96H320V336.02c0-14.213-17.242-21.383-27.313-11.313l-80 79.981c-6.249 6.248-6.249 16.379 0 22.627l80 79.989C302.689 517.308 320 510.3 320 495.989V448h95.88c75.274 0 121.335-82.997 81.408-146.88z" />
    </SvgIcon>
);

const CalendarContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    maxWidth: '1400px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(4),
    },
}));

const MonthNavigation = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 0),
    borderBottom: '1px solid',
    borderColor: theme.palette.divider,
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
    },
}));

const MonthNavigationDialog = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const NavButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)',
    color: 'white',
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(2, 136, 209, 0.2)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        background: 'linear-gradient(135deg, #0277bd 0%, #0288d1 100%)',
        boxShadow: '0 4px 8px rgba(2, 136, 209, 0.3)',
        transform: 'translateY(-1px)',
    },
    '& .MuiButton-startIcon': {
        marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'center',
    },
}));

// Overlay oscuro
const DarkOverlay = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 9998,
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
});

// Componente de notificación estilizado
const NotificationPopup = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    padding: '32px 48px',
    borderRadius: '24px',
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    zIndex: 9999,
    // minWidth: '500px',
    animation: 'slideIn 0.5s ease-out',
    '@keyframes slideIn': {
        '0%': {
            transform: 'translate(-50%, -40%)',
            opacity: 0,
        },
        '100%': {
            transform: 'translate(-50%, -50%)',
            opacity: 1,
        },
    },
}));

const AnimatedEmoji = styled('span')({
    display: 'inline-block',
    animation: 'bounce 1s ease infinite',
    fontSize: '2.5rem',
    '@keyframes bounce': {
        '0%, 100%': {
            transform: 'translateY(0)',
        },
        '50%': {
            transform: 'translateY(-8px)',
        },
    },
});

interface DayCellProps {
    isCurrentDay?: boolean;
    hasRoutes: boolean;
    isWeekend: boolean;
    isCurrentMonth: boolean;
}

const DayCell = styled(Paper, {
    shouldForwardProp: (prop) => !['isCurrentDay', 'hasRoutes', 'isWeekend', 'isCurrentMonth'].includes(prop as string),
})<DayCellProps & { isCurrentMonth: boolean }>(({ theme, isCurrentDay, isCurrentMonth }) => ({
    padding: theme.spacing(1.5),
    margin: theme.spacing(0.25),
    textAlign: 'left',
    minHeight: '160px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: theme.shadows[4],
        zIndex: 1,
    },
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    backgroundColor: isCurrentDay
        ? 'rgba(2, 136, 209, 0.08)'
        : isCurrentMonth
            ? '#ffffff'
            : '#f9fafb',
    border: isCurrentDay
        ? `1px solid ${theme.palette.primary.light}`
        : `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    opacity: isCurrentMonth ? 1 : 0.6,
    overflow: 'hidden',
    position: 'relative',
    '&::after': isCurrentDay ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #0288d1 0%, #03a9f4 100%)',
        borderRadius: '4px 4px 0 0',
    } : {},
    height: '200px', // Fixed height for all cards
    [theme.breakpoints.down('md')]: {
        height: '180px', // Slightly smaller on mobile
        padding: theme.spacing(1),
    },
}));

interface DayNumberProps {
    isCurrentDay?: boolean;
}

const DayNumber = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isCurrentDay',
})<DayNumberProps>(({ theme, isCurrentDay }) => ({
    fontWeight: 600,
    fontSize: '0.95rem',
    color: isCurrentDay ? theme.palette.primary.main : theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: isCurrentDay ? 'rgba(2, 136, 209, 0.1)' : 'transparent',
    transition: 'all 0.2s ease',
}));

const RouteList = styled(Box)(({ theme }) => ({
    overflowY: 'auto',
    flexGrow: 1,
    maxHeight: 'calc(100% - 30px)',
    paddingRight: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.grey[100],
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[400],
        borderRadius: '4px',
        '&:hover': {
            background: theme.palette.grey[500],
        },
    },
    '&::-webkit-scrollbar-corner': {
        background: 'transparent',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
    [theme.breakpoints.down('md')]: {
        maxHeight: 'calc(100% - 25px)',
    },
}));

interface RouteItemProps {
    isOriginRoute?: boolean;
    isDestinyRoute?: boolean;
    children?: React.ReactNode;
}

const RouteItem = styled('div', {
    shouldForwardProp: (prop) => !['isOriginRoute', 'isDestinyRoute'].includes(prop as string),
})<RouteItemProps>(({ theme }) => ({
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    cursor: 'pointer',
    border: '1px solid',
    borderColor: theme.palette.divider,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'translateX(2px)',
        boxShadow: theme.shadows[1],
    },
    '& .MuiTypography-root': {
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        display: 'block',
        fontSize: '0.8rem',
        lineHeight: 1.3,
        margin: 0,
    },
    '&.with-border': {
        borderLeft: '3px solid #ffd700', // Yellow border
        backgroundColor: 'rgba(255, 215, 0, 0.05)',
    },
    '&.with-full-border': {
        border: '1px solid #ffd700', // Yellow border
        backgroundColor: 'rgba(255, 215, 0, 0.05)',
        boxShadow: '0 0 0 1px #ffd700',
    },
    '&.cancelled': {
        backgroundColor: 'rgba(255, 0, 0, 0.03)',
        borderLeft: '3px solid #ff0000', // Red border for cancelled items
        '& .MuiTypography-root': {
            color: theme.palette.text.secondary,
            textDecoration: 'none', // Remove line-through
        },
    },
    '&.completed': {
        backgroundColor: 'rgba(76, 175, 80, 0.05)',
        borderLeft: '3px solid #4CAF50', // Green border for completed
    },
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(0.75, 1),
        '& .MuiTypography-root': {
            fontSize: '0.75rem',
        },
    },
}));

interface StatusDotProps {
    color: string;
}

const StatusDot = styled('span')<StatusDotProps>(({ theme, color }) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: color,
    display: 'inline-block',
    marginRight: theme.spacing(1),
    boxShadow: '0 0 0 2px rgba(255,255,255,0.8)',
    flexShrink: 0,
    transition: 'all 0.2s ease',
}));

const RouteItemContent = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1),
}));

const WeekdayLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'center',
}));

const MonthYearLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.25rem',
    },
}));

const ModalContent = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)', // Replace theme.shadows[5] with a specific shadow
    padding: theme.spacing(4),
    borderRadius: '12px',
}));


const RouteCardsContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    minHeight: '300px',
    overflowY: 'auto',
    maxHeight: '500px', // Ajusta este valor según tus necesidades
}));

const RouteCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isFirst',
})<{ isFirst?: boolean }>(({ theme, isFirst }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
    cursor: 'grab',
    backgroundColor: isFirst ? '#00b4d8' : 'white',
    color: isFirst ? 'white' : 'inherit',
    border: isFirst ? '3px solid #1e88e5' : '1px solid #e0e0e0',
}));

const RouteCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(2),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
}));


const circleAnimation = keyframes`
  0% {
    stroke-dasharray: 0, 100;
  }
  100% {
    stroke-dasharray: 100, 100;
  }
`;

const SuccessModal = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        padding: theme.spacing(3),
        textAlign: 'center',
    },
}));

const AnimatedCheckIcon = styled('svg')({
    width: '60px',
    height: '60px',
});

const AnimatedCircle = styled('circle')({
    fill: 'none',
    stroke: 'green',
    strokeWidth: '3',
    strokeLinecap: 'round',
    animation: `${circleAnimation} 2s ease-in-out forwards`,
});

const OrdersCalendar: React.FC = () => {
    const [editOrder, setEditOrder] = useState<OrdersCalendarState['editOrder']>(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [routeGroups, setRouteGroups] = useState<RouteGroup[]>([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentRouteGroup, setCurrentRouteGroup] = useState<RouteGroup | null>(null);
    const [clients, setClients] = useState<Client[]>([]);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [currentRoute, setCurrentRoute] = useState<Route | null>(null);
    const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
    const [routePoints, setRoutePoints] = useState<google.maps.LatLngLiteral[]>([]);
    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
    const [selectedMarker, setSelectedMarker] = useState<number | null>(null);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [availableClients, setAvailableClients] = useState<Client[]>([]);
    const [hoveredMarker, setHoveredMarker] = useState<number | null>(null);
    const [isRouteUpdated, setIsRouteUpdated] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
    const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
    const [selectedRouteGroup, setSelectedRouteGroup] = useState<RouteGroup | null>(null);

    const [localRouteName, setLocalRouteName] = useState('');
    const [currentRouteIndex, setCurrentRouteIndex] = useState<number>(0);

    // Definición del estado para el centro del mapa
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 9.9281, lng: -84.0907 }); // Ajusta las coordenadas según sea necesario

    // Asegúrate de que mapRef esté correctamente tipado
    const mapRef = useRef<L.Map | null>(null);

    // Define un nuevo estado para las coordenadas decodificadas
    const [decodedRouteCoordinates, setDecodedRouteCoordinates] = useState<[number, number][]>([]);

    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [showReprogrammedOrders, setShowReprogrammedOrders] = useState(false);
    const [currentRouteGroupId, setCurrentRouteGroupId] = useState<string | null>(null);
    const [combinedRoutes, setCombinedRoutes] = useState<Route[]>([]); // Declarar combinedRoutes como estado

    // Function to normalize strings for accent-insensitive search
    const normalizeString = (str: string): string => {
        return str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    };

    const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false); // New state for button enable/disable
    const [routeIdToDelete, setRouteIdToDelete] = useState<string | null>(null); // Estado para el ID de la ruta a eliminar
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Estado para el diálogo de confirmación
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState(''); // Estado para la búsqueda
    const [selectedRouteDetails, setSelectedRouteDetails] = useState<Route | null>(null);
    const [tabValue, setTabValue] = useState(1);
    const [pedidosPreprogramados, setPedidosPreprogramados] = useState<PedidoPreprogramado[]>([]);
    // Agregar el estado para la notificación
    const [showNotification, setShowNotification] = useState(false);
    const [switchStates, setSwitchStates] = useState<{ [key: string]: boolean }>({});
    // const [unificationFlags, setUnificationFlags] = useState<{ [key: string]: boolean }>({});
    const saveButtonRef = useRef<HTMLButtonElement | null>(null);
    const [isRouteOptimized, setIsRouteOptimized] = useState(false);
    const [showNotificationLoading, setShowNotificationLoading] = useState(false);
    const [previewItem, setPreviewItem] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    // 1. Add state at the top of component
    const [deleteRouteUnionDialogOpen, setDeleteRouteUnionDialogOpen] = useState(false);
    const [transportistas, setTransportistas] = useState<Transportista[]>([]);
    const [routeGroupsCache, setRouteGroupsCache] = useState<Map<string, RouteGroup[]>>(new Map());
    const [clientsCache, setClientsCache] = useState<Client[]>([]);
    const [transportistasCache, setTransportistasCache] = useState<Transportista[]>([]);
    const [inventoryItemsCache, setInventoryItemsCache] = useState<InventoryItem[]>([]);
    const [isDroppableMounted, setIsDroppableMounted] = useState(false);
    const [showReprogramConfirm, setShowReprogramConfirm] = useState(false);
    const [reprogramData, setReprogramData] = useState<{ routeGroup: RouteGroup | null, date: Date | null, clientName: string | null, originalRoute: Route | null }>({ routeGroup: null, date: null, clientName: null, originalRoute: null });

    // Filter routes based on search term using a derived value
    const filteredRoutes = React.useMemo(() => {
        if (!searchTerm.trim()) {
            return combinedRoutes;
        }

        const normalizedSearch = normalizeString(searchTerm);
        
        return combinedRoutes.filter(routeUnion => {
            const route = Array.isArray(routeUnion) ? routeUnion[0] : routeUnion;
            if (!route || !route.clientId) return false;
            
            const client = clients.find(c => c.id === route.clientId);
            if (!client) return false;
            
            // Normalize client name for comparison
            const normalizedClientName = normalizeString(client.name || '');
            
            // Check if the search term matches the start of any word in the client name
            return normalizedClientName.split(' ').some(word => 
                word.startsWith(normalizedSearch)
            );
        });
    }, [searchTerm, combinedRoutes, clients]);

    // State for custom notification
    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'info' | 'warning' | 'error';
        title?: string;
    }>({ open: false, message: '', severity: 'info' });

    // Custom Notification Component
    const NotificationPopupReprogram = () => {
        if (!notification.open) return null;

        const severityIcons = {
            success: <CheckCircle sx={{ fontSize: 24, mr: 1.5 }} />,
            error: <Cancel sx={{ fontSize: 24, mr: 1.5, color: '#ff4444' }} />,
            warning: <PriorityHighIcon sx={{ fontSize: 24, mr: 1.5, color: '#ffbb33' }} />,
            info: <InfoIcon sx={{ fontSize: 24, mr: 1.5, color: '#33b5e5' }} />
        };

        const severityColors = {
            success: '#00C851',
            error: '#ff4444',
            warning: '#ffbb33',
            info: '#33b5e5'
        };

        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 24,
                    right: 24,
                    zIndex: 1400,
                    minWidth: 350,
                    maxWidth: '90%',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    backgroundColor: 'white',
                    overflow: 'hidden',
                    transform: notification.open ? 'translateX(0)' : 'translateX(120%)',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderLeft: `4px solid ${severityColors[notification.severity]}`
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: `${severityColors[notification.severity]}15`,
                        mr: 2,
                        flexShrink: 0
                    }}>
                        {severityIcons[notification.severity]}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                            textTransform: 'capitalize'
                        }}>
                            {notification.title || notification.severity}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            {notification.message}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setNotification(prev => ({ ...prev, open: false }))}
                        sx={{
                            ml: 1,
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        height: '4px',
                        backgroundColor: `${severityColors[notification.severity]}40`,
                        animation: (theme) => `
                            ${keyframes`
                                from { width: 100%; }
                                to { width: 0; }
                            `} 4.7s linear forwards
                        `
                    }}
                />
            </Box>
        );
    };
    // Agregar nuevo estado para el caché de pedidos preprogramados
    const [pedidosPreprogramadosCache, setPedidosPreprogramadosCache] = useState<Map<string, PedidoPreprogramado[]>>(new Map());

    useEffect(() => {
        if (editDialogOpen) {
            setIsDroppableMounted(true);
        } else {
            setIsDroppableMounted(false);
        }
    }, [editDialogOpen]);

    useEffect(() => {
        const fetchAndCacheData = async () => {
            setLoading(true);
            try {
                // Siempre cargar los datos estáticos
                const clients = await fetchClients();
                setClientsCache(clients);
                setClients(clients);

                const transportistas = await fetchTransportistas();
                setTransportistasCache(transportistas);
                setTransportistas(transportistas);

                const items = await fetchInventoryItems();
                setInventoryItemsCache(items);
                setInventoryItems(items);

                // Generar clave para el mes actual
                const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;

                // Siempre cargar pedidos preprogramados para el mes actual
                const pedidos = await fetchPedidosPreprogramados();
                setPedidosPreprogramadosCache(prev => new Map(prev).set(monthKey, pedidos));
                setPedidosPreprogramados(pedidos);

                // Siempre cargar route groups para el mes actual
                const routeGroups = await fetchRouteGroups();
                setRouteGroupsCache(prev => new Map(prev).set(monthKey, routeGroups));
                setRouteGroups(routeGroups);

                // Prefetch en segundo plano
                setTimeout(() => {
                    prefetchAdjacentMonths(currentDate);
                }, 0);

            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAndCacheData();
    }, [currentDate]);

    // useEffect(() => {
    //     fetchRouteGroups();
    //     fetchClients();
    //     fetchTransportistas();
    //     fetchInventoryItems();
    //     fetchPedidosPreprogramados();
    // }, [currentDate]);

    useEffect(() => {
        if (editDialogOpen && currentRouteGroup && currentRouteGroup.routesUnion) {
            console.log("editDialogOpen:", editDialogOpen);
            // console.log("currentRouteGroup encontrado:", currentRouteGroup);
            const routesToCombine = currentRouteGroup.routesUnion ? currentRouteGroup.routesUnion : currentRouteGroup.routes;
            // console.log("Selected Date:", selectedDate);
            // console.log("routesToCombine type:", typeof routesToCombine);
            // console.log("routesToCombine is array:", Array.isArray(routesToCombine));
            // console.log("routesToCombine content:", routesToCombine);

            // Obtener el índice de la unión existente
            const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
                const routeDate = date instanceof Timestamp ? date.toDate() : date;
                return routeDate && routeDate.toDateString() === selectedDate?.toDateString();
            });

            // console.log("existingUnionIndex:", existingUnionIndex);

            // Define a type guard to narrow down the type of routesToCombine items
            // type RouteOrRouteTuple = Route | [Route[], Date];


            // Flatten and filter routes
            const hasMatchingRoutes: Route[] = [];

            for (const item of routesToCombine) {
                // console.log('Full item:', item);
                // console.log('Item type:', typeof item);
                // console.log('Is array:', Array.isArray(item));

                if (Array.isArray(item)) {
                    // console.log('Item array details:');
                    item.forEach((subItem, index) => {
                        // console.log(`Item[${index}]:`, subItem);
                        // console.log(`Item[${index}] type:`, typeof subItem);
                        // console.log(`Item[${index}] is array:`, Array.isArray(subItem));
                    });
                }

                // Defensive handling
                if (Array.isArray(item)) {
                    // Flatten and filter routes
                    const routes: Route[] = [];

                    item.forEach(subItem => {
                        // If subItem is an array of routes
                        if (Array.isArray(subItem)) {
                            routes.push(...subItem.filter((route: any): route is Route =>
                                route !== null &&
                                typeof route === 'object' &&
                                'clientId' in route &&
                                'fecha_pedido' in route
                            ));
                        }
                        // If subItem is a single route
                        else if (
                            subItem !== null &&
                            typeof subItem === 'object' &&
                            'clientId' in subItem &&
                            'fecha_pedido' in subItem &&
                            !(subItem instanceof Date)
                        ) {
                            routes.push(subItem as Route);
                        }
                    });

                    const matchingRoutes = routes.filter((route: Route) => {
                        const routeDate = route.fecha_pedido ? new Date(route.fecha_pedido) : null;
                        return areDatesEqual(routeDate, selectedDate);
                    });

                    hasMatchingRoutes.push(...matchingRoutes);
                }
                // If item is a single route
                else if (
                    item !== null &&
                    typeof item === 'object' &&
                    'clientId' in item &&
                    'fecha_pedido' in item &&
                    !(item instanceof Date)
                ) {
                    const routeDate = item.fecha_pedido ? new Date(item.fecha_pedido) : null;
                    if (areDatesEqual(routeDate, selectedDate)) {
                        hasMatchingRoutes.push(item as Route);
                    }
                }
            }

            // console.log("hasMatchingRoutes:", hasMatchingRoutes);
            let newCombinedRoutes = [];

            // Si hay coincidencias, combinarlas con las rutas existentes
            if (hasMatchingRoutes.length > 0 && currentRouteGroup.routesUnion) {
                // Combinar rutas según la existencia de coincidencias
                newCombinedRoutes = hasMatchingRoutes.length > 0
                    ? (existingUnionIndex !== -1
                        ? [...currentRouteGroup.routesUnion[existingUnionIndex][0], ...hasMatchingRoutes]
                        : [...currentRouteGroup.routes, ...hasMatchingRoutes])
                    : (existingUnionIndex !== -1
                        ? [...currentRouteGroup.routesUnion[existingUnionIndex][0]]
                        : [...currentRouteGroup.routes]);
            } else if (currentRouteGroup.routesUnion && currentRouteGroup.routesUnion.length > 0 && existingUnionIndex !== -1) {
                // Si no hay coincidencias, mantener las rutas existentes
                newCombinedRoutes = currentRouteGroup.routesUnion
                    ? [...currentRouteGroup.routesUnion[existingUnionIndex][0]]
                    : [...currentRouteGroup.routes];
            } else {
                newCombinedRoutes = [...currentRouteGroup.routes];
            }
            // Verificar pedidos preprogramados para la fecha actual
            // const updatedCombinedRoutes = [...newCombinedRoutes];
            // pedidosPreprogramados.forEach(pedido => {
            //     const destinyDate = pedido.destinyReprogrammedDate instanceof Timestamp
            //         ? pedido.destinyReprogrammedDate.toDate()
            //         : pedido.destinyReprogrammedDate;

            //     // Verificar si la fecha coincide y si la ruta no existe ya
            //     if (areDatesEqual(destinyDate, selectedDate) &&
            //         !updatedCombinedRoutes.some(route => route.id === pedido.destinyReprogrammedRouteGroupId)) {
            //         // Agregar la ruta reprogramada
            //         // updatedCombinedRoutes.push(pedido.routeRreprogrammed);
            //     }
            // });

            // Solo actualizar si hay un cambio en combinedRoutes
            // if (JSON.stringify(combinedRoutes) !== JSON.stringify(updatedCombinedRoutes)
            // {
            //     setCombinedRoutes(updatedCombinedRoutes);
            // }

            const points = newCombinedRoutes.map(route => {
                const client = clients.find(c => c.id === route.clientId);
                return client && client.googleMapsLink ? extractCoordinates(client.googleMapsLink) : null;
            }).filter((point): point is google.maps.LatLngLiteral => point !== null);

            // Calcular la ruta óptima solo cuando se abre el diálogo y hay puntos
            if (points.length > 0 && editDialogOpen) {
                if (points.length < 2) {
                    setDirections(null);
                    return;
                }

                // // Optimizar el orden de los puntos
                // const optimizedPoints = optimizeRoute(points);

                // // Reordenar combinedRoutes según el orden optimizado de los puntos
                // const reorderedRoutes = optimizedPoints.map(point => {
                //     return combinedRoutes.find(route => {
                //         const client = clients.find(c => c.id === route.clientId);
                //         if (client && client.googleMapsLink) {
                //             const coordinates = extractCoordinates(client.googleMapsLink);
                //             return coordinates && coordinates.lat === point.lat && coordinates.lng === point.lng;
                //         }
                //         return false;
                //     });
                // }).filter((route): route is Route => route !== undefined);

                // // Actualizar combinedRoutes con el nuevo orden
                // setCombinedRoutes(reorderedRoutes);

                calculateOptimalRoute(points).then(() => {
                    if (mapRef.current) {
                        const bounds = L.latLngBounds(points.map(point => [point.lat, point.lng]));
                        mapRef.current.fitBounds(bounds);
                    }
                });
                setCombinedRoutes(newCombinedRoutes);
                setSelectedRoute(newCombinedRoutes[0]);
            }


        } else if (!currentRouteGroup) {
            // console.warn("routeGroup es null, usando arreglo vacío.");
            setCombinedRoutes([]); // Fallback to an empty array if currentRouteGroup is null
        }
    }, [editDialogOpen, currentRouteGroup, clients, selectedDate]); // Ensure dependencies are correct

    // 2. Add delete handler function 
    const handleDeleteRouteUnion = async () => {
        try {
            if (!currentRouteGroup?.id || !selectedDate) {
                toast.error('No se pudo eliminar la ruta: fecha no seleccionada');
                return;
            }

            // Get selected date without time
            const dateToDelete = new Date(selectedDate);
            dateToDelete.setHours(0, 0, 0, 0);
            const dateToDeleteTimestamp = dateToDelete.getTime();

            // Find the routeUnion doc to delete in Firestore
            const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);
            const routesUnionRef = collection(routeGroupRef, 'routesUnion');
            const q = query(routesUnionRef);
            const querySnapshot = await getDocs(q);

            // Find the matching document
            const docToDelete = querySnapshot.docs.find(doc => {
                const data = doc.data();
                const routeDate = data.date instanceof Timestamp ?
                    data.date.toDate() :
                    new Date(data.date);
                routeDate.setHours(0, 0, 0, 0);
                return routeDate.getTime() === dateToDeleteTimestamp;
            });

            if (docToDelete) {
                // Delete the specific routeUnion document
                await deleteDoc(doc(routesUnionRef, docToDelete.id));

                // Update local state
                const updatedRouteGroup = {
                    ...currentRouteGroup,
                    routesUnion: currentRouteGroup.routesUnion.filter((_, index) => {
                        const routeDate = _[1] instanceof Timestamp ?
                            _[1].toDate() :
                            new Date(_[1]);
                        routeDate.setHours(0, 0, 0, 0);
                        return routeDate.getTime() !== dateToDeleteTimestamp;
                    })
                };
                setCurrentRouteGroup(updatedRouteGroup);
            }
            window.location.reload();
            setDeleteRouteUnionDialogOpen(false);
            toast.success('Ruta eliminada exitosamente');

        } catch (error) {
            console.error('Error deleting route union:', error);
            toast.error('Error al eliminar la ruta');
        } finally {
            setDeleteRouteUnionDialogOpen(false);
        }
    };

    const fetchPedidosPreprogramados = async (): Promise<PedidoPreprogramado[]> => {
        try {
            const pedidosRef = collection(db, 'pedidoPreprogramado');
            const q = query(pedidosRef);
            const querySnapshot = await getDocs(q);

            // Crear un mapa para mantener solo el más reciente por routeRreprogrammed.id
            const pedidosPorRouteId = new Map();

            querySnapshot.docs.forEach(doc => {
                const data = doc.data();
                const routeId = data.routeRreprogrammed?.id || data.routeRreprogrammed;
                if (!routeId) return; // Saltar si no tiene routeRreprogrammed

                const pedido = {
                    id: doc.id,
                    originReprogrammedRouteGroupId: data.originReprogrammedRouteGroupId,
                    destinyReprogrammedRouteGroupId: data.destinyReprogrammedRouteGroupId,
                    originReprogrammedDate: data.originReprogrammedDate?.toDate ? data.originReprogrammedDate.toDate() : data.originReprogrammedDate,
                    destinyReprogrammedDate: data.destinyReprogrammedDate?.toDate ? data.destinyReprogrammedDate.toDate() : data.destinyReprogrammedDate,
                    routeRreprogrammed: data.routeRreprogrammed,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date(0))
                };

                // Obtener la fecha más reciente para comparar
                const fechaActual = pedido.originReprogrammedDate || pedido.destinyReprogrammedDate || pedido.timestamp || 0;
                const pedidoExistente = pedidosPorRouteId.get(routeId);
                
                // Si no existe un pedido para este routeId, o si el actual es más reciente
                if (!pedidoExistente) {
                    pedidosPorRouteId.set(routeId, { ...pedido, _fechaComparacion: fechaActual });
                } else {
                    const fechaExistente = pedidoExistente._fechaComparacion || 0;
                    if (fechaActual > fechaExistente) {
                        pedidosPorRouteId.set(routeId, { ...pedido, _fechaComparacion: fechaActual });
                    }
                }
            });

            // Convertir el mapa a un array y eliminar la propiedad temporal _fechaComparacion
            return Array.from(pedidosPorRouteId.values()).map(({ _fechaComparacion, ...pedido }) => pedido)
                .sort((a, b) => {
                    // Ordenar por la fecha más reciente
                    const fechaA = a.originReprogrammedDate || a.destinyReprogrammedDate || a.timestamp || 0;
                    const fechaB = b.originReprogrammedDate || b.destinyReprogrammedDate || b.timestamp || 0;
                    return fechaB - fechaA;
                });
        } catch (error) {
            console.error('Error fetching pedidos preprogramados:', error);
            return [];
        }
    };

    const handleRouteGroupUpdate = async (updatedRouteGroup: RouteGroup) => {
        try {
            // Actualiza el estado del grupo de rutas
            setCurrentRouteGroup(updatedRouteGroup);

            // Si necesitas actualizar combinedRoutes, hazlo aquí
            const updatedRoutes = updatedRouteGroup.routes; // Suponiendo que el grupo de rutas tiene una propiedad 'routes'
            setCombinedRoutes(updatedRoutes);

            // Aquí puedes agregar lógica adicional si es necesario, como guardar en la base de datos
            // await saveRouteGroupToDatabase(updatedRouteGroup);
        } catch (error) {
            console.error('Error updating route group:', error);
            // Manejo de errores, como mostrar un mensaje de error
        }
    };

    const handleCombinedRoutesUpdate = (updatedRoutes: Route[]) => {
        setCombinedRoutes(updatedRoutes); // Actualiza el estado de combinedRoutes
    };

    // const handleCreateOrEditOrder = (route: Route) => {
    //     setCurrentRoute(route);
    //     setIsOrderModalOpen(true);
    // };

    // Filtered routes are now handled by the filteredRoutes memo above

    // Función para manejar la eliminación de la ruta
    const handleDeleteRoute = (routeId: string) => {
        setRouteIdToDelete(routeId); // Establecer el ID de la ruta a eliminar
        setDeleteDialogOpen(true); // Abrir el diálogo de confirmación
    };

    // Función para confirmar la eliminación
    const confirmDeleteRoute = async () => {
        if (routeIdToDelete) {
            try {
                // Filtrar las rutas para eliminar la seleccionada
                const updatedRoutes = combinedRoutes.filter(route => route.clientId !== routeIdToDelete);
                // console.log('....', updatedRoutes)
                setCombinedRoutes(updatedRoutes); // Actualizar el estado con las rutas filtradas
                updateRoutePoints(updatedRoutes);
                setIsSaveButtonEnabled(true);
                toast.success('Ruta eliminada exitosamente'); // Mensaje de éxito
            } catch (error) {
                console.error('Error al eliminar la ruta:', error);
                toast.error('Error al eliminar la ruta'); // Mensaje de error
            } finally {
                setRouteIdToDelete(null); // Resetear el ID de la ruta
                setDeleteDialogOpen(false); // Cerrar el diálogo
            }
        }
    };

    const handleAddRoute = async () => {
        // console.log("Selected Date:", selectedDate);
        // console.log("Current Route Group:", currentRouteGroup);

        // Verifica que selectedDate y currentRouteGroup no sean nulos
        if (!selectedDate) {
            console.error("Fecha seleccionada no válida");
            return;
        }

        if (!currentRouteGroup) {
            // Manejo de errores si no se ha seleccionado una fecha o un grupo de rutas
            console.error("Grupo de rutas no seleccionado");
            return;
        }

        const newRoute: Route = {
            clientId: '', // Reemplaza con el ID del cliente correspondiente
            status: 'pending',
            order: {
                items: [], // Agrega los elementos del pedido según sea necesario
            },
            tipo_pedido: 'Regular',
            fecha_pedido: selectedDate,
            routeGroupId: currentRouteGroup.id || '',
        };

        // Asegúrate de que currentRouteGroup no sea null antes de actualizar
        if (currentRouteGroup) {
            // Add diagnostic logging
            // console.log('Debug: routesUnion', currentRouteGroup.routesUnion);
            // console.log('Debug: selectedDate', selectedDate);
            // console.log('Debug: selectedDate toDateString', selectedDate?.toDateString());

            const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
                // console.log('Debug: Checking route date', date);
                const routeDate = date instanceof Timestamp ? date.toDate() : date;
                // console.log('Debug: Parsed route date', routeDate);
                return routeDate && routeDate.toDateString() === selectedDate?.toDateString();
            });

            if (existingUnionIndex !== -1) {
                // Si existe un elemento en routesUnion que coincide con la fecha seleccionada
                const updatedUnionEntry = {
                    routes: [...currentRouteGroup.routesUnion[existingUnionIndex][0], newRoute], // Agregar el nuevo pedido
                    date: selectedDate // O la fecha que desees asociar
                };

                // Actualiza routesUnion
                const updatedRoutesUnion = [...currentRouteGroup.routesUnion];
                updatedRoutesUnion[existingUnionIndex] = [updatedUnionEntry.routes, updatedUnionEntry.date, false]; // Reemplaza el elemento existente

            } else {
                // Si no existe, crea una nueva entrada en routesUnion
                const newUnionEntry = {
                    routes: [newRoute], // Solo el nuevo pedido
                    date: selectedDate // O la fecha que desees asociar
                };
                // Agregar el nuevo pedido a routesTemp
                currentRouteGroup.routesTemp = [...(currentRouteGroup.routesTemp || []), newRoute]; // Agregar a routesTemp
            }

            // Actualiza combinedRoutes después de agregar la nueva ruta
            const updatedCombinedRoutes = [...combinedRoutes, newRoute];
            setCombinedRoutes(updatedCombinedRoutes); // Actualiza combinedRoutes
        }
    };

    // Asegúrate de que esta función se llame cada vez que cambies las rutas
    const updateAvailableClients = useCallback(() => {
        if (currentRouteGroup) {
            const usedClientIds = new Set(currentRouteGroup.routes.map(route => route.clientId).filter(id => id !== ''));
            const available = clients.filter(client => client.id && !usedClientIds.has(client.id));
            setAvailableClients(available);
        } else {
            setAvailableClients(clients);
        }
    }, [currentRouteGroup, clients]);

    useEffect(() => {
        updateAvailableClients();
    }, [currentRouteGroup, updateAvailableClients]);

    // Función separada para prefetch
    const prefetchAdjacentMonths = async (date: Date) => {
        const nextMonth = new Date(date);
        const prevMonth = new Date(date);
        nextMonth.setMonth(date.getMonth() + 1);
        prevMonth.setMonth(date.getMonth() - 1);

        const nextMonthKey = `${nextMonth.getFullYear()}-${nextMonth.getMonth()}`;
        const prevMonthKey = `${prevMonth.getFullYear()}-${prevMonth.getMonth()}`;

        // Prefetch route groups
        if (!routeGroupsCache.has(nextMonthKey)) {
            const nextData = await fetchRouteGroups();
            setRouteGroupsCache(prev => new Map(prev).set(nextMonthKey, nextData));
        }

        if (!routeGroupsCache.has(prevMonthKey)) {
            const prevData = await fetchRouteGroups();
            setRouteGroupsCache(prev => new Map(prev).set(prevMonthKey, prevData));
        }

        // Prefetch pedidos preprogramados
        if (!pedidosPreprogramadosCache.has(nextMonthKey)) {
            const nextPedidos = await fetchPedidosPreprogramados();
            setPedidosPreprogramadosCache(prev => new Map(prev).set(nextMonthKey, nextPedidos));
        }

        if (!pedidosPreprogramadosCache.has(prevMonthKey)) {
            const prevPedidos = await fetchPedidosPreprogramados();
            setPedidosPreprogramadosCache(prev => new Map(prev).set(prevMonthKey, prevPedidos));
        }
    };

    const fetchRouteGroups = async (): Promise<RouteGroup[]> => {
        try {
            const querySnapshot = await getDocs(collection(db, 'routeGroups'));

            // Usar Promise.all para hacer todas las consultas en paralelo
            const groupsPromises = querySnapshot.docs.map(async (docRef) => {
                const groupData = docRef.data();

                // Ejecutar ambas consultas de subcolecciones en paralelo
                const [routesSnapshot, routesUnionSnapshot] = await Promise.all([
                    getDocs(collection(db, 'routeGroups', docRef.id, 'routes')),
                    getDocs(collection(db, 'routeGroups', docRef.id, 'routesUnion'))
                ]);

                // Procesar los resultados
                const routes: Route[] = routesSnapshot.docs.map(routeDoc => ({
                    id: routeDoc.id,
                    ...routeDoc.data()
                } as Route));

                const routesUnion: [Route[], Date, boolean][] = routesUnionSnapshot.docs.map(routeDoc => {
                    const data = routeDoc.data();
                    return [
                        data.routes || [],
                        data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
                        data.isUnion || false
                    ];
                });

                return {
                    id: docRef.id,
                    name: groupData.name || '',
                    dayOfWeek: groupData.dayOfWeek || 0,
                    weekOfMonth: groupData.weekOfMonth || 0,
                    createdAt: groupData.createdAt instanceof Timestamp
                        ? groupData.createdAt.toDate()
                        : new Date(groupData.createdAt),
                    routes,
                    routesTemp: groupData.routesTemp || [],
                    routesUnion,
                    routeType: groupData.routeType || 'monthly',
                    unificationFlag: groupData.unificationFlag || false
                };
            });

            // Esperar a que todas las promesas se resuelvan
            const groups = await Promise.all(groupsPromises);
            setRouteGroups(groups);
            return groups;

        } catch (error) {
            console.error('Error fetching route groups:', error);
            toast.error('Error al cargar los grupos de rutas');
            return [];
        }
    };

    const fetchClients = async (): Promise<Client[]> => {
        try {
            const clientsRef = collection(db, 'clients');
            const clientsSnap = await getDocs(clientsRef);
            return clientsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Client[];
        } catch (error) {
            console.error('Error fetching clients:', error);
            return [];
        }
    };

    const fetchTransportistas = async (): Promise<Transportista[]> => {
        try {
            const transportistasRef = collection(db, 'transportistas');
            const transportistasSnap = await getDocs(transportistasRef);
            return transportistasSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Transportista[];
        } catch (error) {
            console.error('Error fetching transportistas:', error);
            return [];
        }
    };

    const fetchInventoryItems = async (): Promise<InventoryItem[]> => {
        try {
            const inventoryRef = collection(db, 'inventoryItems');
            const inventorySnap = await getDocs(inventoryRef);
            return inventorySnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as InventoryItem[];
        } catch (error) {
            console.error('Error fetching inventory items:', error);
            return [];
        }
    };

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        const firstDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7;
        const daysToIncludeFromPreviousMonth = firstDayOfWeek;
        const daysToIncludeFromNextMonth = (7 - lastDayOfMonth.getDay() + 6) % 7;

        const startDate = new Date(firstDayOfMonth);
        startDate.setDate(startDate.getDate() - daysToIncludeFromPreviousMonth);

        const endDate = new Date(lastDayOfMonth);
        endDate.setDate(endDate.getDate() + daysToIncludeFromNextMonth);

        const days = [];
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

    const isCurrentMonth = (date: Date) => {
        return date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
    };

    const handleSwitchChange = (dateKey: string) => {
        setSwitchStates(prevStates => ({
            ...prevStates,
            [dateKey]: !prevStates[dateKey]
        }));
    };

    // const fetchUnificationFlag = async (routeGroupId: string) => {
    //     try {
    //         const docRef = doc(db, 'RouteGroups', routeGroupId);
    //         const docSnap = await getDoc(docRef);
    //         if (docSnap.exists()) {
    //             const data = docSnap.data();
    //             const unificationFlag = data.unificationFlag || false;
    // console.log(`Fetched unificationFlag for ${routeGroupId}:`, unificationFlag);
    //             return unificationFlag;
    //         } else {
    //             console.log(`No document found for ${routeGroupId}`);
    //         }
    //     } catch (error) {
    //         console.error("Error fetching unificationFlag:", error);
    //     }
    //     return false;
    // };

    const renderDayCells = () => {
        const days = getDaysInMonth(currentDate);
        const totalCells = 42;
        const weeks: React.ReactNode[] = [];
        let week: React.ReactNode[] = [];

        for (let i = 0; i < totalCells; i++) {
            const date = i < days.length ? days[i] : null;
            const routes = date ? getRoutesForDay(date) : [];
            const isWeekend = date ? (date.getDay() === 0 || date.getDay() === 6) : false;
            const dateKey = date ? date.toISOString().split('T')[0] : ''; // Use date as key

            week.push(
                <Grid item xs key={i} style={{ width: 'calc(100% / 7)', maxWidth: 'calc(100% / 7)', flexBasis: 'calc(100% / 7)' }}>
                    <DayCell
                        isCurrentDay={date ? isCurrentDay(date) : false}
                        hasRoutes={routes.length > 0}
                        isWeekend={isWeekend}
                        isCurrentMonth={date ? isCurrentMonth(date) : false}
                        style={{ width: '100%' }}
                    >
                        {date && (
                            <>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <DayNumber isCurrentDay={isCurrentDay(date)}>{date.getDate()}</DayNumber>
                                    {routes.some(routeGroup => routeGroup.unificationFlag === true) && ( // Solo mostrar el switch si hay rutas con unificationFlag === true
                                        <Switch
                                            checked={switchStates[dateKey] || false}
                                            onChange={() => handleSwitchChange(dateKey)}
                                            color="primary"
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-thumb': {
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: switchStates[dateKey] ? '#87CEEB' : 'default',
                                                },
                                                '& .MuiSwitch-track': {
                                                    height: 16,
                                                    backgroundColor: switchStates[dateKey] ? 'rgba(135, 206, 235, 0.5)' : undefined,
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                                <RouteList>
                                    {routes
                                        .sort((a, b) => {
                                            const aHasOrigin = pedidosPreprogramados?.some(pedido =>
                                                pedido.originReprogrammedRouteGroupId === a.id &&
                                                pedido.originReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.originReprogrammedDate instanceof Timestamp
                                                        ? pedido.originReprogrammedDate.toDate()
                                                        : pedido.originReprogrammedDate,
                                                    date
                                                )
                                            );
                                            const aHasDestiny = pedidosPreprogramados?.some(pedido =>
                                                pedido.destinyReprogrammedRouteGroupId === a.id &&
                                                pedido.destinyReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.destinyReprogrammedDate instanceof Timestamp
                                                        ? pedido.destinyReprogrammedDate.toDate()
                                                        : pedido.destinyReprogrammedDate,
                                                    date
                                                )
                                            );
                                            const bHasOrigin = pedidosPreprogramados?.some(pedido =>
                                                pedido.originReprogrammedRouteGroupId === b.id &&
                                                pedido.originReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.originReprogrammedDate instanceof Timestamp
                                                        ? pedido.originReprogrammedDate.toDate()
                                                        : pedido.originReprogrammedDate,
                                                    date
                                                )
                                            );
                                            const bHasDestiny = pedidosPreprogramados?.some(pedido =>
                                                pedido.destinyReprogrammedRouteGroupId === b.id &&
                                                pedido.destinyReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.destinyReprogrammedDate instanceof Timestamp
                                                        ? pedido.destinyReprogrammedDate.toDate()
                                                        : pedido.destinyReprogrammedDate,
                                                    date
                                                )
                                            );

                                            // Si ambos tienen preprogramación o ninguno la tiene, mantener el orden original
                                            if ((aHasOrigin || aHasDestiny) === (bHasOrigin || bHasDestiny)) {
                                                return 0;
                                            }
                                            // Si a tiene preprogramación y b no, a va primero
                                            if (aHasOrigin || aHasDestiny) {
                                                return -1;
                                            }
                                            // Si b tiene preprogramación y a no, b va primero
                                            return 1;
                                        })
                                        // .filter(async (routeGroup) => {
                                        //     if (!routeGroup.id) {
                                        //         console.warn("RouteGroup ID is undefined");
                                        //         return false;
                                        //     }

                                        //     // Fetch unificationFlag from Firebase
                                        //     const unificationFlag = await fetchUnificationFlag(routeGroup.id);
                                        //     setUnificationFlags(prevFlags => ({
                                        //         ...prevFlags,
                                        //         [routeGroup.id as string]: unificationFlag // Ensure routeGroup.id is a string
                                        //     }));

                                        //     // Debugging: Log the unificationFlag and switch state
                                        //     console.log(`Date: ${dateKey}, RouteGroup: ${routeGroup.id}, UnificationFlag: ${unificationFlag}, SwitchState: ${switchStates[dateKey]}`);

                                        //     // Show only routes with unificationFlag true if switch is active
                                        //     if (switchStates[dateKey]) {
                                        //         return unificationFlag === true;
                                        //     }
                                        //     // Otherwise, show all routes
                                        //     return true;
                                        // })
                                        .filter((routeGroup: RouteGroup) => {
                                            // Filtrar por unificationFlag
                                            // console.log('Evaluando routeGroup:', routeGroup.unificationFlag);
                                            if (switchStates[dateKey]) {
                                                return routeGroup.unificationFlag == true; // Mostrar solo si unificationFlag es true
                                            }
                                            return routeGroup.unificationFlag === undefined || routeGroup.unificationFlag === false; // Mostrar si es undefined o false
                                        })
                                        .map((routeGroup: RouteGroup) => {
                                            const dateMatch = routeGroup.routesUnion?.some(routeItem => {
                                                const routeDate = routeItem?.[1];
                                                const routeDateObj = routeDate instanceof Timestamp ?
                                                    routeDate.toDate() :
                                                    routeDate;
                                                return areDatesEqual(routeDateObj, date);
                                            });

                                            const hasRoutes = dateMatch && Boolean(routeGroup.routesUnion?.[0]?.[0]?.length > 0);

                                            const hasRoutesUnion = (hasRoutes !== undefined ? hasRoutes : false) ||
                                                pedidosPreprogramados?.some(pedido => {
                                                    const destinyDate = pedido.destinyReprogrammedDate instanceof Timestamp
                                                        ? pedido.destinyReprogrammedDate.toDate()
                                                        : pedido.destinyReprogrammedDate;

                                                    return areDatesEqual(destinyDate, date) &&
                                                        !combinedRoutes.some(route => route.id === pedido.destinyReprogrammedRouteGroupId) &&
                                                        pedido.routeRreprogrammed !== undefined;
                                                }) || false;

                                            const hasOriginRoute = pedidosPreprogramados?.some(pedido =>
                                                pedido.originReprogrammedRouteGroupId === routeGroup.id &&
                                                pedido.originReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.originReprogrammedDate instanceof Timestamp
                                                        ? pedido.originReprogrammedDate.toDate()
                                                        : pedido.originReprogrammedDate,
                                                    date
                                                )
                                            );

                                            const hasDestinyRoute = pedidosPreprogramados?.some(pedido =>
                                                pedido.destinyReprogrammedRouteGroupId === routeGroup.id &&
                                                pedido.destinyReprogrammedDate &&
                                                areDatesEqual(
                                                    pedido.destinyReprogrammedDate instanceof Timestamp
                                                        ? pedido.destinyReprogrammedDate.toDate()
                                                        : pedido.destinyReprogrammedDate,
                                                    date
                                                )
                                            );

                                            return (
                                                <RouteItem
                                                    key={routeGroup.id}
                                                    onClick={() => handleEditRoute(routeGroup, date, hasRoutesUnion)}
                                                    className={(() => {
                                                        // Buscar si hay rutas canceladas para la fecha específica
                                                        const hasCancelledRoutes = routeGroup.routesUnion?.some(([routes, routeDate]) => {
                                                            const routeDateObj = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                                                            return areDatesEqual(routeDateObj, date) &&
                                                                Array.isArray(routes) &&
                                                                routes.some(route => route.status === 'cancelled');
                                                        });

                                                        if (hasCancelledRoutes) {
                                                            return 'cancelled';
                                                        } else if (routeGroup.unificationFlag) {
                                                            return 'with-full-border';
                                                        } else if (hasRoutesUnion) {
                                                            return 'with-border';
                                                        }
                                                        return '';
                                                    })()}
                                                    isOriginRoute={hasOriginRoute}
                                                    isDestinyRoute={hasDestinyRoute}
                                                >
                                                    <RouteItemContent>
                                                        {hasOriginRoute && <StatusDot color="red" />}
                                                        {hasDestinyRoute && <StatusDot color="#00FF00" />}
                                                        <Tooltip title={routeGroup.name} arrow>
                                                            <Typography variant="caption" noWrap>
                                                                {routeGroup.name}
                                                            </Typography>
                                                        </Tooltip>
                                                    </RouteItemContent>
                                                </RouteItem>
                                            );
                                        })}
                                </RouteList>
                            </>
                        )}
                    </DayCell>
                </Grid>
            );

            if (week.length === 7 || i === totalCells - 1) {
                weeks.push(
                    <Grid container spacing={1} key={`week-${weeks.length}`}>
                        {week}
                    </Grid>
                );
                week = [];
            }
        }

        return weeks;
    };

    const getRoutesForDay = (date: Date | null) => {
        if (!date) return [];

        return routeGroups.filter((group) => {
            const createdAt = new Date(group.createdAt);
            createdAt.setHours(0, 0, 0, 0);
            const checkDate = new Date(date);
            checkDate.setHours(0, 0, 0, 0);

            const timeDifference = checkDate.getTime() - createdAt.getTime();
            const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
            let weekOfMonth = Math.ceil((checkDate.getDate()) / 7);

            switch (group.routeType) {
                case 'Unification':
                    // Mostrar solo si la fecha coincide con createdAt
                    return createdAt.getTime() === checkDate.getTime();
                case 'weekly':
                    return daysDifference >= 0 && daysDifference % 7 === 0;
                case 'biweekly':
                    return daysDifference >= 0 && daysDifference % 14 === 0;
                case 'monthly':
                    if (group.dayOfWeek !== ((checkDate.getDay() + 6) % 7 + 1)) {
                        // Si estamos en la primera semana y el día no está disponible, intentar la siguiente
                        if (weekOfMonth === 1 && group.weekOfMonth === 1) {
                            weekOfMonth = 2;
                        }
                        // Si estamos en la cuarta semana y el día no está disponible, intentar la anterior
                        else if (weekOfMonth === 4 && group.weekOfMonth === 4) {
                            weekOfMonth = 3;
                        }
                        else {
                            return false;
                        }
                    }
                    return weekOfMonth === group.weekOfMonth;
                case 'bimonthly':
                    if (group.dayOfWeek !== ((checkDate.getDay() + 6) % 7 + 1)) {
                        // Si estamos en la primera semana y el día no está disponible, intentar la siguiente
                        if (weekOfMonth === 1 && group.weekOfMonth === 1) {
                            weekOfMonth = 2;
                        }
                        // Si estamos en la cuarta semana y el día no está disponible, intentar la anterior
                        else if (weekOfMonth === 4 && group.weekOfMonth === 4) {
                            weekOfMonth = 3;
                        }
                        else {
                            return false;
                        }
                    }
                    if (weekOfMonth !== group.weekOfMonth) return false;
                    const monthDifference = (checkDate.getFullYear() - createdAt.getFullYear()) * 12 + checkDate.getMonth() - createdAt.getMonth();
                    return monthDifference % 2 === 0;
                default:
                    return false;
            }
        });
    };

    const handlePrevMonth = () => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() - 1);

        // Limitar a 12 meses atrás
        const minDate = new Date();
        minDate.setMonth(minDate.getMonth() - 2);
        if (newDate >= minDate) {
            setCurrentDate(newDate);
        }
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + 1);

        // Limitar a 12 meses adelante
        const maxDate = new Date();
        maxDate.setMonth(maxDate.getMonth() + 12);
        if (newDate <= maxDate) {
            setCurrentDate(newDate);
        }
    };

    const isCurrentDay = (date: Date | null) => {
        if (!date) return false;
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Función para decodificar la geometría de Polyline
    const decodePolyline = (polyline: string): [number, number][] => {
        const coordinates: [number, number][] = [];
        let index = 0, len = polyline.length;
        let lat = 0, lng = 0;

        while (index < len) {
            let b, shift = 0, result = 0;
            do {
                b = polyline.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            const dlat = ((result >> 1) ^ -(result & 1));
            lat += dlat;

            shift = 0;
            result = 0;
            do {
                b = polyline.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            const dlng = ((result >> 1) ^ -(result & 1));
            lng += dlng;

            coordinates.push([lng * 1e-5, lat * 1e-5]); // Convertir a coordenadas
        }

        return coordinates;
    };

    const calculateDistance = (p1: { lat: number; lng: number }, p2: { lat: number; lng: number }): number => {
        const R = 6371; // Radio de la Tierra en kilómetros
        const dLat = (p2.lat - p1.lat) * Math.PI / 180;
        const dLon = (p2.lng - p1.lng) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distancia en kilómetros
    };

    const calculateTotalDistance = (route: { lat: number; lng: number }[]): number => {
        let totalDistance = 0;
        for (let i = 0; i < route.length - 1; i++) {
            totalDistance += calculateDistance(route[i], route[i + 1]);
        }
        return totalDistance;
    };

    const twoOptSwap = (route: { lat: number; lng: number }[], i: number, k: number): { lat: number; lng: number }[] => {
        const newRoute = [...route];
        while (i < k) {
            const temp = newRoute[i];
            newRoute[i] = newRoute[k];
            newRoute[k] = temp;
            i++;
            k--;
        }
        return newRoute;
    };

    const optimizeRoute = (points: { lat: number; lng: number }[]): { lat: number; lng: number }[] => {
        if (points.length <= 2) return points;

        let bestRoute = [...points];
        let bestDistance = calculateTotalDistance(bestRoute);
        let improved = true;

        while (improved) {
            improved = false;
            for (let i = 1; i < points.length - 2; i++) {
                for (let k = i + 1; k < points.length - 1; k++) {
                    const newRoute = twoOptSwap([...bestRoute], i, k);
                    const newDistance = calculateTotalDistance(newRoute);

                    if (newDistance < bestDistance) {
                        bestRoute = newRoute;
                        bestDistance = newDistance;
                        improved = true;
                    }
                }
            }
        }

        return bestRoute;
    };

    const calculateOptimalRoute = async (points: { lat: number; lng: number }[]) => {
        if (points.length < 2) {
            setDirections(null);
            return;
        }

        // Optimizar el orden de los puntos
        const optimizedPoints = optimizeRoute(points);
        const coordinates = optimizedPoints.map(point => `${point.lng},${point.lat}`).join(';');
        const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full`;

        try {
            const response = await axios.get(osrmUrl);
            const route = response.data.routes[0];

            if (route) {
                const decodedCoordinates = decodePolyline(route.geometry);
                const formattedCoordinates: [number, number][] = decodedCoordinates.map(coord => [coord[1], coord[0]] as [number, number]);
                setDecodedRouteCoordinates(formattedCoordinates);
                setDirections(response.data);

                // Calcular y mostrar la distancia total
                const totalDistance = route.distance / 1000; // Convertir a kilómetros
                toast.success(`Ruta optimizada: ${totalDistance.toFixed(2)} km`);
            } else {
                console.error("No se encontraron rutas en la respuesta de la API.");
                setDirections(null);
            }
        } catch (error) {
            toast.error('Error al calcular la ruta óptima');
        }
    };

    const calculateCombinedRoutes = (routeGroup: RouteGroup, date: Date | null) => {
        // Verifica si routesUnion existe y tiene elementos
        if (routeGroup.routesUnion && routeGroup.routesUnion.length > 0) {
            // Filtra las rutas que coinciden con la fecha seleccionada
            const matchingRoutes = routeGroup.routesUnion.filter(([routes, routeDate]) => {
                const resetTime = (date: Date) => {
                    const newDate = new Date(date);
                    newDate.setHours(0, 0, 0, 0);
                    return newDate;
                };

                const routeDateWithoutTime = resetTime(routeDate instanceof Timestamp ? routeDate.toDate() : routeDate);
                const selectedDateWithoutTime = date ? resetTime(date) : null;

                return selectedDateWithoutTime && routeDateWithoutTime &&
                    routeDateWithoutTime.getTime() === selectedDateWithoutTime.getTime();
            });

            // Combina todas las rutas de las entradas que coinciden
            const combinedRoutes = matchingRoutes.flatMap(([routes]) => routes);
            return combinedRoutes;
        } else {
            // Si no hay routesUnion, devuelve las rutas actuales
            return routeGroup.routes;
        }
    };

    // Add type for status
    type RouteStatus = 'pending' | 'completed' | 'cancelled';

    const handleRouteChange = (index: number, field: keyof Route, value: any) => {
        if (!currentRouteGroup || availableClients.length === 0) return;

        // Verify client assignment
        if (field === 'clientId') {
            const isClientAlreadyAssigned = combinedRoutes.some(
                (route, idx) => idx !== index && route.clientId === value
            );
            if (isClientAlreadyAssigned) {
                toast.error('Este cliente ya está asignado a otra ruta');
                return;
            }
        }

        // Validate index
        if (index < 0 || index >= combinedRoutes.length) {
            console.error("Índice fuera de rango:", index);
            return;
        }

        // Update routes with typed status
        let updatedRoutes = [...combinedRoutes];
        const currentRoute = updatedRoutes[index] || {};
        updatedRoutes[index] = {
            ...currentRoute,
            [field]: value,
            status: 'pending' as RouteStatus
        };

        const hasMatchingRoutes = currentRouteGroup.routesTemp?.some(route =>
            route.fecha_pedido?.toDateString() === selectedDate?.toDateString()
        );

        // Update route group with proper typing
        const updatedRouteGroup: RouteGroup = {
            ...currentRouteGroup,
            routesTemp: hasMatchingRoutes ?
                (currentRouteGroup.routesTemp || []).map((route, idx) =>
                    idx === index ? {
                        ...(route || {}),
                        [field]: value,
                        status: 'pending' as RouteStatus
                    } : route
                ) :
                currentRouteGroup.routesTemp,
            routes: !hasMatchingRoutes ?
                (currentRouteGroup.routes || []).map((route, idx) =>
                    idx === index ? {
                        ...(route || {}),
                        [field]: value,
                        status: 'pending' as RouteStatus
                    } : route
                ) :
                currentRouteGroup.routes
        };

        // Update routesUnion with proper typing
        if (currentRouteGroup.routesUnion) {
            const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
                const routeDate = date instanceof Timestamp ? date.toDate() : date;
                const resetTime = (date: Date) => {
                    const newDate = new Date(date);
                    newDate.setHours(0, 0, 0, 0);
                    return newDate;
                };

                const selectedDateWithoutTime = selectedDate ? resetTime(selectedDate) : null;
                const routeDateWithoutTime = resetTime(routeDate);

                return selectedDateWithoutTime && routeDateWithoutTime &&
                    routeDateWithoutTime.getTime() === selectedDateWithoutTime.getTime();
            });

            if (existingUnionIndex !== -1) {
                const updatedUnionRoutes = [...(currentRouteGroup.routesUnion[existingUnionIndex][0] || [])];
                updatedUnionRoutes[index] = {
                    ...(updatedUnionRoutes[index] || {}),
                    [field]: value,
                    id: updatedUnionRoutes[index]?.id || '',
                    order: { items: [] } as Order,
                    status: 'pending' as RouteStatus
                };

                updatedRouteGroup.routesUnion = [
                    ...currentRouteGroup.routesUnion.slice(0, existingUnionIndex),
                    [updatedUnionRoutes, selectedDate || new Date(), false],
                    ...currentRouteGroup.routesUnion.slice(existingUnionIndex + 1)
                ];
            }
        }

        setCurrentRouteGroup(updatedRouteGroup);
        setCombinedRoutes(updatedRoutes);

        if (field === 'clientId') {
            updateRoutePoints(updatedRoutes);
            updateAvailableClients();
        }

        setIsRouteUpdated(true);
        setIsSaveButtonEnabled(true);
    };

    const updateRoutePoints = useCallback(async (routes: Route[]) => {
        const points: { lat: number; lng: number }[] = []; // Cambiar el tipo a un objeto simple
        for (const route of routes) {
            const client = clients.find(c => c.id === route.clientId);
            if (client && client.googleMapsLink) {
                const coordinates = extractCoordinates(client.googleMapsLink);
                if (coordinates) {
                    points.push(coordinates);
                }
            }
        }
        setRoutePoints(points);
        if (points.length > 0) {
            await calculateOptimalRoute(points);
        } else {
            setDirections(null);
        }
    }, [clients]);

    const handleDragEnd = useCallback(async (result: DropResult) => {
        if (!result.destination || !combinedRoutes) return;

        const { source, destination } = result;

        // Prevent dragging if not first or last
        const isFirstOrLast = (index: number) => index === 0 || index === combinedRoutes.length - 1;
        if (!isFirstOrLast(source.index) && !isFirstOrLast(destination.index)) return;
        if (source.index === destination.index) return;

        // Create a new list of routes
        const newRoutes = Array.from(combinedRoutes);
        const [reorderedItem] = newRoutes.splice(source.index, 1);
        newRoutes.splice(destination.index, 0, reorderedItem);

        // Si el destino es la primera posición, excluir el último elemento de la optimización
        const routesToOptimize = (destination.index === 0) || (destination.index === newRoutes.length - 1) ? newRoutes.slice(0, -1) : newRoutes;

        // Optimizar el orden de las rutas basado en las coordenadas
        const routesWithCoordinates = routesToOptimize.map(route => {
            const client = clients.find(c => c.id === route.clientId);
            if (client && client.googleMapsLink) {
                const coordinates = extractCoordinates(client.googleMapsLink);
                return { ...route, coordinate: coordinates };
            }
            return route;
        });

        // Filter out routes without valid coordinates and ensure proper typing
        const validRoutes = routesWithCoordinates.filter((route): route is Route => {
            return route.coordinate !== null && route.coordinate !== undefined;
        });
        const optimizedRoutes = await optimizeWaypoints(validRoutes);

        // Si el destino es la primera posición, agregar el último elemento de newRoutes al final
        const finalRoutes = (destination.index === 0) || (destination.index === newRoutes.length - 1)
            ? [...optimizedRoutes, newRoutes[newRoutes.length - 1]]
            : optimizedRoutes;

        // Update the orderIndex for visual representation
        const updatedRoutesWithOrder = finalRoutes.map((route, index) => ({
            ...route,
            orderIndex: index + 1,
        }));

        // setCombinedRoutes(updatedRoutesWithOrder);

        // Update the selected marker index and hovered marker
        setSelectedMarker(null);
        setHoveredMarker(null);

        setIsRouteUpdated(true);
        setIsSaveButtonEnabled(true);

        const points: { lat: number; lng: number }[] = []; // Cambiar el tipo a un objeto simple
        for (const route of updatedRoutesWithOrder) {
            const client = clients.find(c => c.id === route.clientId);
            if (client && client.googleMapsLink) {
                const coordinates = extractCoordinates(client.googleMapsLink);
                if (coordinates) {
                    points.push(coordinates);
                }
            }
        }
        setRoutePoints(points);
        if (points.length > 0) {
            if (points.length < 2) {
                setDirections(null);
                return;
            }

            // Optimizar el orden de los puntos
            const optimizedPoints = optimizeRoute(points);

            // Reordenar combinedRoutes según el orden optimizado de los puntos
            const reorderedRoutes = optimizedPoints.map(point => {
                return combinedRoutes.find(route => {
                    const client = clients.find(c => c.id === route.clientId);
                    if (client && client.googleMapsLink) {
                        const coordinates = extractCoordinates(client.googleMapsLink);
                        return coordinates && coordinates.lat === point.lat && coordinates.lng === point.lng;
                    }
                    return false;
                });
            }).filter((route): route is Route => route !== undefined);

            // Actualizar combinedRoutes con el nuevo orden
            setCombinedRoutes(reorderedRoutes);

            const coordinates = optimizedPoints.map(point => `${point.lng},${point.lat}`).join(';');
            const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full`;

            try {
                const response = await axios.get(osrmUrl);
                const route = response.data.routes[0];

                if (route && route.geometry) {
                    const decodedCoordinates = decodePolyline(route.geometry);
                    // console.log('Coordenadas decodificadas:', decodedCoordinates);
                    const formattedCoordinates: [number, number][] = decodedCoordinates.map(coord => [coord[1], coord[0]] as [number, number]);
                    // console.log('Coordenadas formateadas:', formattedCoordinates);
                    setDecodedRouteCoordinates(formattedCoordinates);
                    setDirections(response.data);

                    // Calcular y mostrar la distancia total
                    const totalDistance = route.distance / 1000; // Convertir a kilómetros
                    toast.success(`Ruta optimizada: ${totalDistance.toFixed(2)} km`);
                } else {
                    console.error("No se encontró geometría en la ruta o no hay rutas disponibles.");
                    setDirections(null);
                    setDecodedRouteCoordinates([]);
                }
            } catch (error) {
                toast.error('Error al calcular la ruta óptima');
            }
        } else {
            setDirections(null);
        }

        // updateRoutePoints(updatedRoutesWithOrder);

        // Update map markers and avatar numbers
        // const points = updatedRoutesWithOrder.map(route => {
        //     const client = clients.find(c => c.id === route.clientId);
        //     return client && client.googleMapsLink ? extractCoordinates(client.googleMapsLink) : null;
        // }).filter((point): point is google.maps.LatLngLiteral => point !== null);

        // setRoutePoints(points);

        // // Recalculate optimal route
        // if (points.length > 0) {
        //     await calculateOptimalRoute(points);
        //     toast.success('Ruta optimizada exitosamente');
        // } else {
        //     setDirections(null);
        // }

    }, [combinedRoutes, selectedDate, currentRouteGroup, clients]);

    // Function to remove a specific route from a group
    const convertRoutesForFirestore = (group: RouteGroup, routeToRemove?: string) => {
        try {
            // console.log('Converting routes for group:', group.name);
            // console.log('Route to remove:', routeToRemove);

            // Log all route IDs in the group
            // console.log('Group route IDs:', group.routes.map(route => route.id));
            // console.log('Group routesUnion route IDs:',
            //     group.routesUnion
            //         ? group.routesUnion.flatMap(([routes]) => routes.map(route => route.id))
            //         : 'No routesUnion'
            // );

            // Utility function to safely check if an item is a Route
            const isRoute = (item: any): item is Route =>
                item && typeof item === 'object' && 'id' in item && 'clientId' in item;

            // Utility function to filter routes
            const filterRoutes = (routes: any[], routeToRemove?: string): Route[] => {
                const flattenAndFilter = (items: any[]): Route[] => {
                    return items.reduce<Route[]>((acc, item) => {
                        // Handle nested arrays
                        if (Array.isArray(item)) {
                            acc.push(...flattenAndFilter(item));
                            return acc;
                        }

                        // Filter out routes with matching ID
                        if (isRoute(item) && (!routeToRemove || item.id !== routeToRemove)) {
                            acc.push(item);
                        }

                        return acc;
                    }, []);
                };

                return flattenAndFilter(routes);
            };

            // Main conversion logic
            const convertedGroup = (() => {
                // Determine source routes
                const sourceRoutes = group.routesUnion?.length
                    ? group.routesUnion
                    : group.routes;

                // Filter routes
                const filteredRoutes = filterRoutes(sourceRoutes, routeToRemove);

                // Prepare routesUnion
                const updatedRoutesUnion = group.routesUnion?.length
                    ? group.routesUnion
                        .map(([routes, date]) => {
                            const filteredUnionRoutes = filterRoutes(routes, routeToRemove);
                            return filteredUnionRoutes.length
                                ? [filteredUnionRoutes, date]
                                : null;
                        })
                        .filter((entry): entry is [Route[], Date] => entry !== null)
                    : filteredRoutes.length
                        ? [[filteredRoutes, selectedDate || new Date()]]
                        : [];

                // Construct converted group
                return {
                    ...group,
                    routes: group.routes
                        ? group.routes.filter(route =>
                            !routeToRemove || route.id !== routeToRemove
                        )
                        : [],
                    routesTemp: group.routesTemp
                        ? group.routesTemp.filter(route =>
                            !routeToRemove || route.id !== routeToRemove
                        )
                        : [],
                    routesUnion: group.routesUnion
                };
            })();
            // console.log('Converted group:', JSON.stringify(convertedGroup, null, 2));
            return convertedGroup;
        } catch (conversionError) {
            console.error('Error in convertRoutesForFirestore:', conversionError);
            throw conversionError;
        }
    };

    // const convertToRoute = (route: Route): Route => {
    //     // Ensure all required properties are present and of the correct type
    //     return {
    //         id: route.id,
    //         clientId: route.clientId,
    //         status: route.status,
    //         order: route.order,
    //         coordinate: route.coordinate,
    //         orderIndex: route.orderIndex,
    //         tipo_pedido: route.tipo_pedido,
    //         fecha_pedido: route.fecha_pedido,
    //         routeGroupId: route.routeGroupId,
    //         pedidoPreprogramado: route.pedidoPreprogramado ? {
    //             originReprogrammedRouteGroupId: route.pedidoPreprogramado.originReprogrammedRouteGroupId,
    //             destinyReprogrammedRouteGroupId: route.pedidoPreprogramado.destinyReprogrammedRouteGroupId,
    //             originReprogrammedDate: route.pedidoPreprogramado.originReprogrammedDate,
    //             destinyReprogrammedDate: route.pedidoPreprogramado.destinyReprogrammedDate,
    //             routeRreprogrammed: route
    //         } : undefined
    //     };
    // };

    const handleConfirmReprogram = async () => {
        if (!reprogramData.routeGroup || !reprogramData.date || !reprogramData.originalRoute) return;

        setShowReprogramConfirm(false);
        try {
            setShowNotification(true);
            console.log('Reprogramando ruta:', reprogramData.routeGroup);
            console.log('Reprogramando fecha:', reprogramData.date);
            console.log('Reprogramando ruta original:', reprogramData.originalRoute);
            await executeReprogramming(
                reprogramData.routeGroup,
                reprogramData.date,
                reprogramData.originalRoute // Pass the original route to executeReprogramming
            );
            console.log('Reprogramando ruta exitosamente:', reprogramData.routeGroup);
            console.log('Reprogramando fecha exitosamente:', reprogramData.date);
            console.log('Reprogramando ruta original exitosamente:', reprogramData.originalRoute);
        } catch (error) {
            console.error('Error in handleConfirmReprogram:', error);
            toast.error('Error al confirmar la reprogramación');
        } finally {
            setReprogramData({ routeGroup: null, date: null, clientName: null, originalRoute: null });
        }
    };

    const handleCancelReprogram = () => {
        setShowReprogramConfirm(false);
        setReprogramData({ routeGroup: null, date: null, clientName: null, originalRoute: null });
    };

    const fetchClientName = async (clientId: string): Promise<string> => {
        try {
            const clientDoc = await getDoc(doc(db, 'clients', clientId));
            if (clientDoc.exists()) {
                const clientData = clientDoc.data() as Client;
                return clientData.name || 'Cliente';
            }
            return 'Cliente';
        } catch (error) {
            console.error('Error fetching client:', error);
            return 'Cliente';
        }
    };

    const handleEditRouteReprogramar = async (routeGroup: RouteGroup, date: Date) => {
        if (!selectedRoute?.clientId) {
            toast.error('No se pudo obtener la información del cliente');
            return;
        }

        try {
            const clientName = await fetchClientName(selectedRoute.clientId);
            // Check if client already has an order in the target route
            const targetDate = date.toDateString();
            const clientHasOrder = routeGroup.routesUnion?.some(union => {
                const unionDate = union[1].toDateString();
                return unionDate === targetDate &&
                    union[0].some(route => route.clientId === selectedRoute.clientId);
            });

            if (clientHasOrder) {
                // Show custom error popup for 5 seconds
                setNotification({
                    open: true,
                    title: 'Error de programación',
                    message: `No se puede reprogramar el pedido a esta ruta porque ya existe un pedido para ${clientName}`,
                    severity: 'error'
                });

                // Auto-close after 5 seconds
                const timer = setTimeout(() => {
                    setNotification(prev => ({ ...prev, open: false }));
                }, 5000);

                return () => clearTimeout(timer);
            }
            setCurrentRoute(selectedRoute); // Set the current route for the dialog
            setReprogramData({
                routeGroup,
                date,
                clientName,
                originalRoute: selectedRoute // Include the original route in the state
            });
            setShowReprogramConfirm(true);
        } catch (error) {
            console.error('Error preparing reprogram dialog:', error);
            toast.error('Error al preparar la reprogramación');
        }
    };

    const executeReprogramming = async (routeGroup: RouteGroup, date: Date, originalRoute: Route) => {
        setShowNotification(true);
        try {
            if (!currentRouteGroup?.id || !originalRoute?.id) {
                toast.error('Datos de ruta incompletos');
                return;
            }

            const batch = writeBatch(db);

            // 1. Remove from source route group's routesUnion
            const sourceGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);
            const sourceRoutesUnionRef = collection(sourceGroupRef, 'routesUnion');

            // Find and update the source routesUnion
            const sourceRoutesUnionSnap = await getDocs(sourceRoutesUnionRef);
            let sourceUpdated = false;

            for (const docSnap of sourceRoutesUnionSnap.docs) {
                const unionData = docSnap.data();
                if (unionData.date?.toDate().toDateString() === selectedDate?.toDateString()) {
                    // Remove the route from the routes array
                    const updatedRoutes = (unionData.routes || []).filter(
                        (r: Route) => r.id !== originalRoute.id
                    );

                    batch.update(doc(sourceRoutesUnionRef, docSnap.id), {
                        routes: updatedRoutes
                    });
                    sourceUpdated = true;
                    break;
                }
            }

            // 2. Add to destination route group's routesUnion
            if (!routeGroup?.id) {
                throw new Error('ID del grupo de rutas de destino no válido');
            }
            const destGroupRef = doc(db, 'routeGroups', routeGroup.id);
            const destRoutesUnionRef = collection(destGroupRef, 'routesUnion');

            // Check if destination date exists in routesUnion
            const destRoutesUnionSnap = await getDocs(destRoutesUnionRef);
            let destUnionExists = false;
            let destUnionId = '';

            for (const docSnap of destRoutesUnionSnap.docs) {
                const unionData = docSnap.data();
                if (unionData.date?.toDate().toDateString() === date.toDateString()) {
                    destUnionExists = true;
                    destUnionId = docSnap.id;
                    break;
                }
            }

            // Create updated route with new date and preserve all original properties
            // First create the route with all properties
            const updatedRoute: any = {
                ...originalRoute,
                id: originalRoute.id,
                clientId: originalRoute.clientId,
                status: originalRoute.status,
                order: originalRoute.order,
                coordinate: originalRoute.coordinate ? { ...originalRoute.coordinate } : null,
                orderIndex: originalRoute.orderIndex || 0,
                tipo_pedido: originalRoute.tipo_pedido || '',
                fecha_pedido: Timestamp.fromDate(date), // Keep as Timestamp for Firestore
                routeGroupId: routeGroup.id || originalRoute.routeGroupId || '',
                note_admin: originalRoute.note_admin || null,
                note_driver: originalRoute.note_driver || null,
                delivery_completed: originalRoute.delivery_completed || false,
                container_values: originalRoute.container_values ? [...originalRoute.container_values] : [],
                updatedBy: originalRoute.updatedBy || null,
                deliveryFileUrl: originalRoute.deliveryFileUrl || null,
                lenght_container_values: originalRoute.lenght_container_values || 0
            };

            // Remove any undefined values
            Object.keys(updatedRoute).forEach(key => {
                if (updatedRoute[key] === undefined) {
                    delete updatedRoute[key];
                }
            });

            // Convert the object back to the Route type
            const cleanRoute: Route = updatedRoute;

            if (destUnionExists) {
                // Add to existing routesUnion
                const destUnionRef = doc(destRoutesUnionRef, destUnionId);
                batch.update(destUnionRef, {
                    routes: arrayUnion(cleanRoute)
                });
            } else {
                // Create new routesUnion entry
                batch.set(doc(destRoutesUnionRef), {
                    date: Timestamp.fromDate(date),
                    routes: [cleanRoute]
                });
            }

            // 3. Create pedidoPreprogramado record
            const pedidoPreprogramadoRef = doc(collection(db, 'pedidoPreprogramado'));
            batch.set(pedidoPreprogramadoRef, {
                originReprogrammedRouteGroupId: currentRouteGroup.id,
                destinyReprogrammedRouteGroupId: routeGroup.id,
                originReprogrammedDate: Timestamp.fromDate(selectedDate || new Date()),
                destinyReprogrammedDate: Timestamp.fromDate(date),
                routeRreprogrammed: originalRoute,
                createdAt: Timestamp.now()
            });

            await batch.commit();

            // Refresh data
            fetchRouteGroups();
            window.location.reload();
            toast.success('Pedido reprogramado exitosamente');

        } catch (error) {
            console.error('Error al reprogramar el pedido:', error);
            toast.error('Error al reprogramar el pedido');
            throw error;
        } finally {
            setShowNotification(false);
            handleCloseCalendarDialog();
            handleCloseEditDialog();
        }
    };

    const handleEditRoute = useCallback((routeGroup: RouteGroup, date: Date, hasRoutesUnion: boolean) => {
        // console.log('handleEditRoute llamado con:', {
        //     routeGroup: {
        //         id: routeGroup.id,
        //         name: routeGroup.name,
        //         routes: routeGroup.routes?.length,
        //         routesTemp: routeGroup.routesTemp?.length,
        //         routesUnion: routeGroup.routesUnion?.length
        //     },
        //     date
        // });

        setIsRouteOptimized(hasRoutesUnion);

        // Verificar si hay rutas disponibles en routes o routesTemp
        const availableRoutes = routeGroup.routesUnion && routeGroup.routesUnion.length > 0
            ? routeGroup.routesUnion
            : routeGroup.routes || [];

        // console.log('Rutas disponibles:', availableRoutes.map(route => {
        //     // Check if route is an array or a single route
        //     if (Array.isArray(route)) {
        //         // If it's an array, log a warning and return null or skip
        //         console.warn('Unexpected route type: Array');
        //         return null;
        //     }

        //     // Ensure route is a valid object with the expected properties
        //     return {
        //         id: route.id || 'N/A',
        //         clientId: route.clientId || 'N/A',
        //         fecha_pedido: route.fecha_pedido || null,
        //         formattedDate: route.fecha_pedido ?
        //             (route.fecha_pedido instanceof Timestamp
        //                 ? route.fecha_pedido.toDate().toISOString()
        //                 : new Date(route.fecha_pedido).toISOString())
        //             : 'No date'
        //     };
        // }).filter(Boolean));

        // console.log('Fecha de búsqueda:', date.toISOString());

        // Si no hay rutas en routesUnion, intentar construirlo desde routes
        if (!routeGroup.routesUnion || routeGroup.routesUnion.length === 0) {
            // console.log('No hay routesUnion, intentando construir desde routes existentes');

            // If routesUnion exists and has entries
            const routesForDate: Route[] = routeGroup.routesUnion && routeGroup.routesUnion.length > 0
                ? routeGroup.routesUnion
                    .flatMap(([routes]) => routes as Route[]) // Explicitly type cast routes
                    .filter((route: Route) => { // Explicitly type the route parameter
                        if (!route?.fecha_pedido) {
                            // console.warn('Ruta sin fecha:', route);
                            return false;
                        }
                        // Compare dates ignoring the time
                        return isSameDay(new Date(route.fecha_pedido), date);
                    })
                : (availableRoutes as Route[]).filter((route: Route) => {
                    if (!route?.fecha_pedido) {
                        // console.warn('Ruta sin fecha:', route);
                        return false;
                    }
                    return isSameDay(new Date(route.fecha_pedido), date);
                });

            // console.log('Rutas encontradas para la fecha:', routesForDate);

            if (routesForDate.length > 0) {
                setSelectedRoute(routesForDate[0]);
                // console.log('Ruta seleccionada:', routesForDate[0]);
            } else {
                // console.log('No se encontraron rutas para la fecha seleccionada en routesUnion');
                // Select the first route from the array, or null if the array is empty
                const selectedRouteFromGroup = routeGroup.routes.length > 0 ? routeGroup.routes[0] : null;
                // console.log('Selected Route from Group:', selectedRouteFromGroup);
                setSelectedRoute(selectedRouteFromGroup);
            }
        } else {
            // Lógica original para routesUnion
            const routeForDate = routeGroup.routesUnion?.find(([_, routeDate]) => {
                const normalizedRouteDate = routeDate instanceof Timestamp
                    ? routeDate.toDate()
                    : routeDate instanceof Date
                        ? routeDate
                        : new Date(routeDate);

                return areDatesEqual(normalizedRouteDate, date);
            });

            if (routeForDate && routeForDate[0].length > 0) {
                // console.log('Rutas encontradas para la fecha en routesUnion:', routeForDate[0]);
                setSelectedRoute(routeForDate[0][0]);
            } else {
                // console.log('No se encontraron rutas para la fecha seleccionada en routesUnion');
            }
        }

        setCurrentRouteGroup(routeGroup);
        setLocalRouteName(routeGroup.name);
        setEditDialogOpen(true);
        setSelectedDate(date);

        const updatedRoutes = [...combinedRoutes]; // Asegúrate de que esto esté definido aquí

        // Cambiar la forma en que se obtienen los puntos
        // const points = updatedRoutes.map(route => {
        //     const client = clients.find(c => c.id === route.clientId);
        //     if (client && client.googleMapsLink) {
        //         return extractCoordinates(client.googleMapsLink);
        //     }
        //     return null;
        // }).filter((point): point is google.maps.LatLngLiteral => point !== null);

        updateRoutePoints(updatedRoutes); // Llama a la función para actualizar los puntos

        // Calcular la ruta óptima y actualizar las coordenadas decodificadas
        // if (points.length > 0) {
        //     calculateOptimalRoute(points).then(() => {
        //         // Ajustar el mapa para mostrar la ruta más corta
        //         if (mapRef.current) {
        //             const bounds = L.latLngBounds(points.map(point => [point.lat, point.lng]));
        //             mapRef.current.fitBounds(bounds); // Ajusta el mapa para mostrar la ruta más corta
        //         }
        //     });
        // } else {
        //     setDirections(null); // Si no hay puntos, no hay dirección
        // }

    }, [updateRoutePoints, calculateOptimalRoute, clients, combinedRoutes]);

    // }, [calculateOptimalRoute, clients, combinedRoutes]);

    const handleNameChange = useCallback((newName: string) => {
        if (currentRouteGroup) {
            setCurrentRouteGroup(prevGroup => ({
                ...prevGroup!,
                name: newName
            }));
        }
    }, [currentRouteGroup]);

    const handleCloseEditDialog = () => {
        setShowReprogrammedOrders(false);
        setEditDialogOpen(false);
        setCurrentRouteGroup(null);
        setDirections(null);
        setRoutePoints([]);
        setSearchTerm('');
    };
    const safeUpdateObject = (obj: Record<string, any>) => {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([_, value]) => value !== undefined)
        );
    }

    // const handleSaveRouteGroup = async () => {
    //     if (!combinedRoutes || !currentRouteGroup || !currentRouteGroup.id) {
    //         console.error("No hay un grupo de rutas válido para guardar");
    //         toast.error('Error al guardar el grupo de rutas');
    //         return;
    //     }

    //     // Combina las rutas de currentRouteGroup.routes con las de routesTemp que coinciden si hay coincidencias
    //     const combinedRoutesT = currentRouteGroup.routesTemp?.filter(route =>
    //         route.fecha_pedido?.toDateString() === selectedDate?.toDateString()
    //     );

    //     const hasMatchingRoutes = combinedRoutesT && combinedRoutesT.length > 0;

    //     const batch = writeBatch(db);
    //     const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);

    //     // Preparar un objeto seguro para actualización
    //     const updateData = safeUpdateObject({
    //         name: currentRouteGroup.name,
    //         dayOfWeek: currentRouteGroup.dayOfWeek,
    //         weekOfMonth: currentRouteGroup.weekOfMonth,
    //         routeType: currentRouteGroup.routeType,
    //         createdAt: currentRouteGroup.createdAt,
    //     });

    //     // Actualiza la información del grupo de rutas
    //     batch.update(routeGroupRef, updateData);

    //     const routesRef = collection(routeGroupRef, 'routesUnion');
    //     const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
    //         const routeDate = date instanceof Timestamp ? date.toDate() : date;
    //         return routeDate && routeDate.toDateString() === selectedDate?.toDateString();
    //     });

    //     // Camino 1: Agregar nuevos pedidos
    //     if (existingUnionIndex > 0 || hasMatchingRoutes || (combinedRoutes && combinedRoutes.length != (currentRouteGroup?.routes?.length || 0))) {
    //         const newUnionEntry = {
    //             routes: combinedRoutes.map((route, index) => ({
    //                 ...route,
    //                 orderIndex: index + 1 // Asignar un índice de orden
    //             })),
    //             date: selectedDate
    //         };

    //         // Usar batch.set para insertar o actualizar el nuevo elemento
    //         const newUnionRef = doc(routesRef, `union_${selectedDate?.toISOString()}`); // Genera un ID único
    //         batch.set(newUnionRef, newUnionEntry); // Usar set para insertar

    //         // Actualizar currentRouteGroup.routesUnion
    //         if (selectedDate) {
    //             currentRouteGroup.routesUnion.push([newUnionEntry.routes, selectedDate]); // Agregar la nueva entrada
    //         }
    //     } else if (existingUnionIndex === -1) {
    //         // Camino 5: Si hay cambios en routesUnion 
    //         const existingUnionRef = doc(routesRef, `union_${selectedDate?.toISOString()}`);
    //         if (existingUnionRef) {
    //             batch.set(existingUnionRef, {
    //                 routes: combinedRoutes,
    //                 date: selectedDate
    //             });

    //             // Actualizar currentRouteGroup.routesUnion
    //             currentRouteGroup.routesUnion[existingUnionIndex] = [
    //                 combinedRoutes,
    //                 selectedDate ? selectedDate : new Date()
    //             ];
    //         }
    //     } else {
    //         // Camino 4: Si no existe, actualiza routes de RouteGroup
    //         batch.update(routeGroupRef, { routesUnion: combinedRoutes });
    //     }

    //     // Ejecutar el batch
    //     await batch.commit();
    //     handleCloseEditDialog();
    //     fetchRouteGroups();

    //     // Refrescar la página
    //     window.location.reload();
    // };
    const handleSaveRouteGroup = async () => {
        if (!combinedRoutes || !currentRouteGroup || !currentRouteGroup.id) {
            console.error("No hay un grupo de rutas válido para guardar");
            toast.error('Error al guardar el grupo de rutas');
            return;
        }

        const batch = writeBatch(db);
        const routeGroupRef = doc(db, 'routeGroups', currentRouteGroup.id);

        // Preparar un objeto seguro para actualización
        const updateData = safeUpdateObject({
            name: currentRouteGroup.name,
            dayOfWeek: currentRouteGroup.dayOfWeek,
            weekOfMonth: currentRouteGroup.weekOfMonth,
            routeType: currentRouteGroup.routeType,
            createdAt: currentRouteGroup.createdAt,
        });

        // Actualiza la información del grupo de rutas
        batch.update(routeGroupRef, updateData);

        const routesRef = collection(routeGroupRef, 'routesUnion');
        const existingUnionIndex = currentRouteGroup.routesUnion.findIndex(([_, date]) => {
            const routeDate = date instanceof Timestamp ? date.toDate() : date;
            return routeDate && routeDate.toDateString() === selectedDate?.toDateString();
        });

        if (existingUnionIndex !== -1) {
            const routesUnionSnapshot = await getDocs(routesRef);
            const matchingDoc = routesUnionSnapshot.docs.find(doc => {
                const docData = doc.data();
                const docDate = docData.date instanceof Timestamp ? docData.date.toDate() : docData.date;
                return docDate && docDate.toDateString() === selectedDate?.toDateString();
            });

            if (!matchingDoc) {
                console.error("No se encontró el documento en Firestore");
                toast.error('Error al actualizar el grupo de rutas');
                return;
            }

            const updatedRoutes = [...combinedRoutes];

            // for (const route of updatedRoutes) {
            //     if (!route.id) {
            //         // Use subcollection 'routes' under routeGroups instead of root collection
            //         const routesSubcollectionRef = collection(routeGroupRef, 'routes');
            //         const newRouteRef = doc(routesSubcollectionRef);

            //         const routeData = safeUpdateObject({
            //             clientId: route.clientId || '',
            //             status: route.status || 'pending',
            //             order: route.order || { items: [] },
            //             tipo_pedido: route.tipo_pedido || 'Regular',
            //             fecha_pedido: route.fecha_pedido || selectedDate,
            //             routeGroupId: currentRouteGroup.id,
            //             orderIndex: route.orderIndex || 0
            //         });

            //         if (routeData) {
            //             batch.set(newRouteRef, routeData);
            //             route.id = newRouteRef.id;
            //         }
            //     }
            // }

            const updatedUnionEntry = {
                routes: updatedRoutes.map((route, index) => ({
                    ...route,
                    orderIndex: index + 1
                })),
                date: selectedDate
            };

            const existingUnionRef = doc(routesRef, matchingDoc.id);
            batch.update(existingUnionRef, updatedUnionEntry);

            currentRouteGroup.routesUnion[existingUnionIndex] = [
                updatedUnionEntry.routes,
                selectedDate || new Date(),
                true
            ];
        } else {
            console.error("No se encontró un documento de routesUnion para actualizar");
            toast.error('Error al actualizar el grupo de rutas');
            return;
        }

        await batch.commit();
        toast.success('Rutas actualizadas exitosamente');
        handleCloseEditDialog();
        fetchRouteGroups();
        window.location.reload();
    };

    const extractCoordinates = (url: string): google.maps.LatLngLiteral | null => {
        // Actualizar la expresión regular para coincidir con el formato de la URL de Google Maps
        const regex = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/; // Cambiado para buscar 'q='
        const match = url.match(regex);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }

        // Buscar coordenadas en formato @
        const atMatch = url.match(/@(-?\d+\.\d+),\s*(-?\d+\.\d+)(?:[,/]\d+[mz])?/);
        if (atMatch) {
            const lat = parseFloat(atMatch[1]);
            const lng = parseFloat(atMatch[2]);
            console.log('Coordenadas encontradas (formato @):', { lat, lng });
            return { lat, lng };
        }

        // console.log("No se pudieron extraer coordenadas de:", url);
        return null;
    };

    const InfoSection = styled(Paper)(({ theme }) => ({
        padding: theme.spacing(3),
        backgroundColor: '#f5f5f5',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        marginBottom: theme.spacing(3),
    }));

    const SectionTitle = styled(Typography)(({ theme }) => ({
        color: '#00b4d8',
        fontWeight: 'bold',
        marginBottom: theme.spacing(2),
    }));

    const StyledFormControl = styled(FormControl)(({ theme }) => ({
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#00b4d8',
            },
            '&:hover fieldset': {
                borderColor: '#0095b6',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#00b4d8',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#00b4d8',
        },
    }));

    const IconWrapper = styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        '& > svg': {
            marginRight: theme.spacing(1),
            color: '#00b4d8',
        },
    }));

    // Enhanced date comparison function
    const areDatesEqual = (date1: Date | Timestamp | null | undefined, date2: Date | null | undefined): boolean => {
        if (!date1 || !date2) return false;

        const normalizedDate1 = date1 instanceof Timestamp ? date1.toDate() : date1;
        const normalizedDate2 = date2;

        return (
            normalizedDate1.getFullYear() === normalizedDate2.getFullYear() &&
            normalizedDate1.getMonth() === normalizedDate2.getMonth() &&
            normalizedDate1.getDate() === normalizedDate2.getDate()
        );
    };

    const renderMapFilter = () => {
        return (
            <>
                <style>
                    {`
                    .leaflet-bottom.leaflet-right {
                        display: none; /* Oculta la atribución de Leaflet */
                    }
                    `}
                </style>
                <MapContainer
                    center={mapCenter}
                    zoom={10}
                    style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                    ref={mapRef} // Asegúrate de que mapRef esté correctamente asignado
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {filteredRoutes.length === 0 ? (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            textAlign: 'center',
                            zIndex: 1000,
                        }}>
                            <Typography variant="h6" style={{ color: '#00b4d8' }}>
                                No hay resultados que coincidan con su búsqueda.
                            </Typography>
                        </div>
                    ) : (filteredRoutes.map((routeUnion, index) => {
                        const route = Array.isArray(routeUnion) ? routeUnion[0] : routeUnion; // Asegúrate de que sea un array

                        const client = clients.find(c => c.id === route.clientId);
                        const point = client ? extractCoordinates(client.googleMapsLink) : null;
                        if (!point) {
                            // console.warn(`No se pudo extraer coordenadas para el cliente con ID: ${route.clientId}`);
                            return null;
                        }
                        // console.log(`Punto para el cliente ${client?.name}:`, point); // Verificar las coordenadas

                        return (
                            <Marker
                                key={route.id || index}
                                position={point}
                                icon={L.divIcon({
                                    className: 'custom-marker',
                                    html: `
                                <div style="position: relative; width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; transition: transform 0.3s, box-shadow 0.3s;">
                                    <div class="marker-circle" style="width: 40px; height: 40px; border-radius: 50%; background-color:  ${((route.orderIndex === 1 || route.orderIndex === filteredRoutes.length) || (index === 0 || index === filteredRoutes.length - 1)) ? '#FFA500' : '#00b4d8'}; opacity: 0.8; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);"></div>
                                    <div class="marker-number" style="position: absolute; color: white; font-weight: bold; font-size: 16px; transition: transform 0.3s ease;">
                                        ${route.orderIndex >= 1 ? route.orderIndex : index + 1}
                                    </div>
                                </div>
                            `,
                                    iconSize: [60, 60],
                                })}
                                eventHandlers={{
                                    mouseover: (e) => {
                                        const marker = e.target;
                                        const circle = marker.getElement().querySelector('.marker-circle');
                                        const number = marker.getElement().querySelector('.marker-number');
                                        if (circle) {
                                            circle.style.transform = 'scale(1.5)'; // Aumentar el tamaño del círculo
                                        }
                                        if (number) {
                                            number.style.transform = 'scale(1.5)'; // Aumentar el tamaño del número
                                        }
                                    },
                                    mouseout: (e) => {
                                        const marker = e.target;
                                        const circle = marker.getElement().querySelector('.marker-circle');
                                        const number = marker.getElement().querySelector('.marker-number');
                                        if (circle) {
                                            circle.style.transform = 'scale(1)'; // Volver al tamaño original
                                        }
                                        if (number) {
                                            number.style.transform = 'scale(1)'; // Volver al tamaño original
                                        }
                                    },
                                }}>
                                <Popup>
                                    <div style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                        width: '200px',
                                        height: '100px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative', // Añadir posición relativa
                                    }}>
                                        <div style={{ position: 'absolute', left: '-29px' }}> {/* Ajustar la posición del círculo */}
                                            <Circle
                                                center={point}
                                                radius={20}
                                                fillColor="#00b4d8"
                                                fillOpacity={0.5}
                                            />
                                        </div>
                                        <div style={{
                                            position: 'absolute',
                                            transform: 'translate(-396%, -158%)',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            backgroundColor: 'transparent',
                                            pointerEvents: 'none',
                                        }}>
                                            <Avatar sx={{
                                                bgcolor: ((route.orderIndex === 1 || route.orderIndex === filteredRoutes.length) || (index === 0 || index === filteredRoutes.length - 1)) ? '#FFA500' : '#00b4d8',
                                                color: 'white',
                                                width: 24,
                                                height: 24,
                                            }}>
                                                {route.orderIndex >= 1 ? route.orderIndex : index + 1}
                                                {/* {route.orderIndex} */}
                                            </Avatar>
                                        </div>
                                        <strong style={{ color: '#00b4d8', fontSize: '1.1rem' }}>
                                            {clients.find(c => c.id === route.clientId)?.name}
                                        </strong>
                                        <br />
                                        <Button
                                            variant="contained"
                                            onClick={() => handleViewDetails(route, index)}
                                            sx={{
                                                backgroundColor: '#00b4d8',
                                                color: 'white',
                                                width: '100%', // Botón ocupa todo el ancho
                                                height: '40px',
                                                minWidth: '120px', // Ancho mínimo fijo para todos los botones
                                                marginTop: '10px',
                                                '&:hover': {
                                                    backgroundColor: '#0095b6',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </Button>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    }))
                    }
                    {/* Aquí se dibuja la ruta más corta */}
                    {
                        decodedRouteCoordinates.length > 0 && (
                            <Polyline
                                positions={decodedRouteCoordinates} // Usar las coordenadas decodificadas
                                color="#00b4d8" // Color de la ruta
                                weight={5} // Grosor de la línea
                                opacity={0.7} // Opacidad de la línea
                            />
                        )
                    }
                </MapContainer >
            </>
        );
    };

    const renderMap = () => {
        return (
            <>
                <style>
                    {`
                    .leaflet-bottom.leaflet-right {
                        display: none; /* Oculta la atribución de Leaflet */
                    }
                    `}
                </style>
                <MapContainer
                    center={mapCenter}
                    zoom={10}
                    style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                    ref={mapRef} // Asegúrate de que mapRef esté correctamente asignado
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {combinedRoutes.map((route, index) => {
                        const client = clients.find(c => c.id === route.clientId);
                        const point = client ? extractCoordinates(client.googleMapsLink) : null;
                        if (!point) {
                            // console.warn(`No se pudo extraer coordenadas para el cliente con ID: ${route.clientId}`);
                            return null;
                        }
                        // console.log(`Punto para el cliente ${client?.name}:`, point); // Verificar las coordenadas

                        return (
                            <Marker
                                key={index}
                                position={point}
                                icon={L.divIcon({
                                    className: 'custom-marker',
                                    html: `
                                <div style="position: relative; width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; transition: transform 0.3s, box-shadow 0.3s;">
                                    <div class="marker-circle" style="width: 40px; height: 40px; border-radius: 50%; background-color: ${(index === 0 || index === combinedRoutes.length - 1) ? '#FFA500' : '#00b4d8'}; opacity: 0.8; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);"></div>
                                    <div class="marker-number" style="position: absolute; color: white; font-weight: bold; font-size: 16px; transition: transform 0.3s ease;">
                                        ${index + 1}
                                    </div>
                                </div>
                            `,
                                    iconSize: [60, 60],
                                })}
                                eventHandlers={{
                                    mouseover: (e) => {
                                        const marker = e.target;
                                        const circle = marker.getElement().querySelector('.marker-circle');
                                        const number = marker.getElement().querySelector('.marker-number');
                                        if (circle) {
                                            circle.style.transform = 'scale(1.5)'; // Aumentar el tamaño del círculo
                                        }
                                        if (number) {
                                            number.style.transform = 'scale(1.5)'; // Aumentar el tamaño del número
                                        }
                                    },
                                    mouseout: (e) => {
                                        const marker = e.target;
                                        const circle = marker.getElement().querySelector('.marker-circle');
                                        const number = marker.getElement().querySelector('.marker-number');
                                        if (circle) {
                                            circle.style.transform = 'scale(1)'; // Volver al tamaño original
                                        }
                                        if (number) {
                                            number.style.transform = 'scale(1)'; // Volver al tamaño original
                                        }
                                    },
                                }}>
                                <Popup>
                                    <div style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                        width: '200px',
                                        height: '100px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative', // Añadir posición relativa
                                    }}>
                                        <div style={{ position: 'absolute', left: '-29px' }}> {/* Ajustar la posición del círculo */}
                                            <Circle
                                                center={point}
                                                radius={20}
                                                fillColor="#00b4d8"
                                                fillOpacity={0.5}
                                            />
                                        </div>
                                        <div style={{
                                            position: 'absolute',
                                            transform: 'translate(-396%, -158%)',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            backgroundColor: 'transparent',
                                            pointerEvents: 'none',
                                        }}>
                                            <Avatar sx={{
                                                bgcolor: (index === 0 || index === combinedRoutes.length - 1) ? '#FFA500' : '#00b4d8',
                                                color: 'white',
                                                width: 24,
                                                height: 24,
                                            }}>
                                                {index + 1} {/* Mostrar el número de posición usando orderIndex */}
                                            </Avatar>
                                        </div>
                                        <strong style={{ color: '#00b4d8', fontSize: '1.1rem' }}>
                                            {clients.find(c => c.id === route.clientId)?.name}
                                        </strong>
                                        <br />
                                        <Button
                                            variant="contained"
                                            onClick={() => handleViewDetails(route, index)}
                                            sx={{
                                                backgroundColor: '#00b4d8',
                                                color: 'white',
                                                width: '100%', // Botón ocupa todo el ancho
                                                height: '40px',
                                                minWidth: '120px', // Ancho mínimo fijo para todos los botones
                                                marginTop: '10px',
                                                '&:hover': {
                                                    backgroundColor: '#0095b6',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </Button>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })
                    }
                    {/* Aquí se dibuja la ruta más corta */}
                    {
                        decodedRouteCoordinates.length > 0 && (
                            <Polyline
                                positions={decodedRouteCoordinates} // Usar las coordenadas decodificadas
                                color="#00b4d8" // Color de la ruta
                                weight={5} // Grosor de la línea
                                opacity={0.7} // Opacidad de la línea
                            />
                        )
                    }
                </MapContainer >
            </>
        );
    };

    const handleCreateSingleRoutesUnion = useCallback(async () => {
        setShowNotification(true);

        try {
            if (!selectedDate || !currentRouteGroup?.id) {
                toast.error('No hay fecha o grupo de ruta seleccionado');
                return;
            }

            // 1. Verificar si ya existe una ruta para esta fecha
            const routesUnionRef = collection(db, `routeGroups/${currentRouteGroup.id}/routesUnion`);
            const existingQuery = query(
                routesUnionRef,
                where('date', '==', selectedDate)
            );
            const existingRoutes = await getDocs(existingQuery);

            if (!existingRoutes.empty) {
                toast.info('Ya existe una ruta para esta fecha');
                return;
            }

            // 2. Obtener las rutas base del grupo
            const routesRef = collection(db, `routeGroups/${currentRouteGroup.id}/routes`);
            const routesSnapshot = await getDocs(routesRef);
            const routes: Route[] = routesSnapshot.docs.map(routeDoc => ({
                id: routeDoc.id,
                ...routeDoc.data()
            } as Route));

            // 3. Crear el nuevo documento de routesUnion
            const newRoutesUnionRef = doc(collection(db, `routeGroups/${currentRouteGroup.id}/routesUnion`));
            await setDoc(newRoutesUnionRef, {
                routes,
                date: selectedDate,
                isUnificationFlag: false,
            });

            // 4. Actualizar el estado local con el tipo correcto
            const updatedRouteGroup: RouteGroup = {
                ...currentRouteGroup,
                routesUnion: [
                    ...currentRouteGroup.routesUnion,
                    [routes, selectedDate, false] as [Route[], Date, boolean]
                ]
            };

            setCurrentRouteGroup(updatedRouteGroup);
            setCombinedRoutes(routes);
            setIsRouteOptimized(true);

            // Continuar con la lógica de creación de rutas...
            console.log('Rutas obtenidas:', routes);

            toast.success('Ruta creada exitosamente');
            window.location.reload();

            // 5. Recargar los datos
            // await fetchRouteGroups();
            // handleEditRoute(currentRouteGroup, selectedDate, true);

        } catch (error) {
            console.error('Error al crear rutas:', error);
            toast.error('Error al crear la ruta');
            throw error;
        } finally {
            setShowNotification(false);
            setLoading(false);
        }
        // }, [selectedDate, currentRouteGroup?.id]);
    }, [selectedDate, currentRouteGroup, fetchRouteGroups, handleEditRoute]);
    const handleSaveReschedule = async () => {
        if (!selectedRoute || !rescheduleDate) return;

        try {
            // Aquí iría la lógica para mover el pedido a la nueva fecha
            // Por ejemplo, crear un nuevo pedido preprogramado para la fecha seleccionada
            // y eliminar el pedido actual de la ruta actual

            // Ejemplo de implementación:
            /*
            const pedidoPreprogramado: PedidoPreprogramado = {
                clientId: selectedRoute.clientId,
                fecha: rescheduleDate,
                routeGroupId: currentRouteGroup?.id || '',
                routeId: selectedRoute.id,
                status: 'pending',
                createdAt: new Date()
            };

            await addDoc(collection(db, 'pedidosPreprogramados'), pedidoPregramado);
            
            // Eliminar la ruta actual
            await handleDeleteRoute(selectedRoute.clientId);
            */

            toast.success('Pedido preprogramado correctamente');
            setCalendarDialogOpen(false);
            setSelectedRoute(null);
            setRescheduleDate(null);
        } catch (error: unknown) {
            console.error('Error al preprogramar el pedido:', error);
            toast.error('Error al preprogramar el pedido');
        }
    };

    const renderEditDialog = () => (
        <AnimatePresence>
            {editDialogOpen && (
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ height: '100%' }}
                >
                    <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth={false} fullWidth sx={{
                        '& .MuiDialog-paper': {
                            maxWidth: '2400px',
                            height: '100vh',
                            borderRadius: '12px',
                        },
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            backgroundColor: 'transparent'
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0, 180, 216, 0.05)',
                            borderRadius: '10px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgb(0, 180, 216)',
                            borderRadius: '10px',
                            border: '2px solid transparent',
                            backgroundClip: 'content-box',
                            '&:hover': {
                                backgroundColor: 'rgb(0, 119, 182)',
                                cursor: 'pointer'
                            }
                        },
                        // Firefox scrollbar styling
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgb(0, 180, 216) rgba(0, 180, 216, 0.05)'
                    }}>
                        <DialogTitleWrapper>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}> {/* Añadido para espaciar los botones */}
                                <Avatar sx={{
                                    bgcolor: '#FFA500',
                                    color: 'white',
                                    mr: 2,
                                    width: 47,
                                    height: 47,
                                    fontSize: 27
                                }}>
                                    {combinedRoutes.length}
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                    Total de Pedidos
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '70%', gap: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', marginRight: '11rem' }}>
                                    {format(selectedDate || new Date(), "EEEE", { locale: es })} {/* Día de la semana */}
                                </Typography>
                                <DateCircle sx={{ margin: '0 0.5rem' }}>
                                    {format(selectedDate || new Date(), 'd')} {/* Día del mes */}
                                </DateCircle>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', width: '50%' }}>
                                    de {format(selectedDate || new Date(), "MMMM 'de' yyyy", { locale: es })} {/* Mes y año */}
                                </Typography>
                            </Box>
                        </DialogTitleWrapper>
                        <DialogContent sx={{
                            p: 3,
                            flex: 1, // Changed from fixed height to flex: 1
                            minHeight: 0, // Important for nested flex containers
                            display: 'flex', // Enable flex layout
                            flexDirection: 'column', // Stack children vertically
                        }}>
                            {tabValue === 1 && (
                                <Box sx={{
                                    flex: 1, // Take up all available space
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0, // Important for nested flex containers
                                }}>
                                    <Grid container spacing={2} sx={{
                                        flex: 1, // Changed from fixed height
                                        minHeight: 0 // Important for nested flex containers
                                    }}>
                                        <Grid item xs={12} md={8} sx={{ height: { xs: '50vh', md: '100%' }, minHeight: 0, order: { xs: 2, md: 1 } }}>
                                            <Box sx={{
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '12px',
                                                padding: '24px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                                height: '100%', // Ocupa toda la altura
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexShrink: 0,
                                                    backgroundColor: '#f5f5f5',
                                                    padding: 2,
                                                    borderRadius: 1,
                                                }}>
                                                    <Box>
                                                        <SectionTitle variant="h6" sx={{ marginBottom: 1 }}>
                                                            <IconWrapper>
                                                                <FormatListBulleted />
                                                                Lista de Pedidos - {currentRouteGroup?.name}
                                                            </IconWrapper>
                                                        </SectionTitle>

                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            backgroundColor: 'white',
                                                            padding: 1,
                                                            borderRadius: 1,
                                                            boxShadow: 1
                                                        }}>
                                                            <LocalShipping sx={{ color: '#00b4d8', marginRight: 1 }} />
                                                            <Box>
                                                                <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                                                    Transportista
                                                                </Typography>
                                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                                    {transportistas.find(t =>
                                                                        t.listaRutas?.includes(currentRouteGroup?.id || '')
                                                                    )?.name || 'No asignado'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <Tooltip title={showReprogrammedOrders ? "Ocultar pedidos reprogramados" : "Mostrar pedidos reprogramados"}>
                                                            <Button
                                                                startIcon={showReprogrammedOrders ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                                onClick={() => setShowReprogrammedOrders(!showReprogrammedOrders)}
                                                                variant="outlined"
                                                                disabled={!pedidosPreprogramados?.length || !pedidosPreprogramados.some(pedido => 
                                                                    (pedido.originReprogrammedRouteGroupId === currentRouteGroup?.id) &&
                                                                    pedido.routeRreprogrammed
                                                                )}
                                                                sx={{
                                                                    borderColor: '#ff4d4f',
                                                                    color: '#ff4d4f',
                                                                    borderRadius: '8px',
                                                                    padding: '8px 16px',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                                    transition: 'all 0.3s ease',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    '&:hover': {
                                                                        backgroundColor: '#fff2f0',
                                                                        borderColor: '#ff4d4f',
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        borderColor: '#e0e0e0',
                                                                        color: '#999999'
                                                                    }
                                                                }}
                                                            >
                                                                {showReprogrammedOrders ? 'Ocultar' : 'Mostrar'} Reprogramados
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title={!isRouteOptimized ? "Optimiza la ruta primero" : currentRouteGroup?.unificationFlag ? "No se puede Agregar Cliente en ruta unificada" : "Agregar nuevo cliente"}>
                                                            <span>
                                                                <Button
                                                                    startIcon={<AddIcon />}
                                                                    onClick={handleAddRoute}
                                                                    variant="contained"
                                                                    disabled={!isRouteOptimized || currentRouteGroup?.unificationFlag}
                                                                    sx={{
                                                                        backgroundColor: '#00b4d8',
                                                                        borderRadius: '8px',
                                                                        padding: '8px 16px',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                        transition: 'all 0.3s ease',
                                                                        textTransform: 'none',
                                                                        fontWeight: 600,
                                                                        '&:hover': {
                                                                            backgroundColor: '#0077be',
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                                                        },
                                                                        '&.Mui-disabled': {
                                                                            backgroundColor: '#e0e0e0',
                                                                            color: '#999999'
                                                                        }
                                                                    }}
                                                                >
                                                                    Agregar Cliente
                                                                </Button>
                                                            </span>
                                                        </Tooltip>

                                                        {isRouteOptimized ? (
                                                            <Tooltip title={currentRouteGroup?.unificationFlag ? "No se puede eliminar una ruta unificada" : "Eliminar ruta actual"}>
                                                                <span> {/* Wrapper necesario para que el Tooltip funcione con botón deshabilitado */}
                                                                    <Button
                                                                        startIcon={<DeleteIcon />}
                                                                        onClick={() => setDeleteRouteUnionDialogOpen(true)}
                                                                        variant="contained"
                                                                        disabled={currentRouteGroup?.unificationFlag}
                                                                        sx={{
                                                                            backgroundColor: '#ff4444',
                                                                            borderRadius: '8px',
                                                                            padding: '8px 16px',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                            transition: 'all 0.3s ease',
                                                                            textTransform: 'none',
                                                                            fontWeight: 600,
                                                                            '&:hover': {
                                                                                backgroundColor: '#cc0000',
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                backgroundColor: 'rgba(255, 68, 68, 0.5)',
                                                                                color: 'rgba(255, 255, 255, 0.7)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Eliminar Ruta
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Crear nueva ruta para este día">
                                                                <Button
                                                                    startIcon={<AddIcon />}
                                                                    onClick={handleCreateSingleRoutesUnion}
                                                                    variant="contained"
                                                                    sx={{
                                                                        backgroundColor: '#4CAF50',
                                                                        borderRadius: '8px',
                                                                        padding: '8px 16px',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                        transition: 'all 0.3s ease',
                                                                        textTransform: 'none',
                                                                        fontWeight: 600,
                                                                        '&:hover': {
                                                                            backgroundColor: '#388E3C',
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                                                        }
                                                                    }}
                                                                >
                                                                    Crear Ruta
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Buscar por nombre..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    sx={{
                                                        mb: 2,
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: 'white'
                                                        }
                                                    }}
                                                />
                                                <Box sx={{
                                                    flexGrow: 1,
                                                    overflowY: 'auto',
                                                    // Estilo para el scrollbar
                                                    '&::-webkit-scrollbar': {
                                                        width: '8px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: '#f1f1f1',
                                                        borderRadius: '4px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: '#888',
                                                        borderRadius: '4px',
                                                        '&:hover': isRouteOptimized ? {
                                                            background: '#555',
                                                        } : undefined,
                                                    },
                                                }}>
                                                    <DragDropContext onDragEnd={handleDragEnd}>
                                                        {isDroppableMounted && (
                                                            <Droppable droppableId="routes" isDropDisabled={!isRouteOptimized}>
                                                                {(provided) => (
                                                                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                                                                        {filteredRoutes.map((routeUnion, index) => {
                                                                            const route = Array.isArray(routeUnion) ? routeUnion[0] : routeUnion; // Asegúrate de que sea un array
                                                                            const routeId = route ? 'id' in route ? route.id : '' : ''; // Acceso seguro al id
                                                                            if (!route) return null; // Saltar si la ruta es indefinida

                                                                            return (
                                                                                <Draggable
                                                                                    key={routeId || `route-${index}`} // Usa routeId directamente
                                                                                    draggableId={routeId || `route-${index}`}
                                                                                    index={index}
                                                                                    isDragDisabled={index === 0 || index === filteredRoutes.length - 1 || !isRouteOptimized} // Deshabilitar arrastre para todos menos el primero y el último
                                                                                >
                                                                                    {(provided, snapshot) => (
                                                                                        <Paper
                                                                                            ref={provided.innerRef}
                                                                                            {...provided.draggableProps}
                                                                                            {...provided.dragHandleProps}
                                                                                            elevation={snapshot.isDragging ? 6 : 1}
                                                                                            sx={{
                                                                                                mb: 2,
                                                                                                p: 2,
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                backgroundColor: snapshot.isDragging ? '#e1f5fe' : 'white',
                                                                                                borderLeft: `6px solid ${index === 0 || index === filteredRoutes.length - 1 ? '#FFA500' : '#00b4d8'}`,
                                                                                                transition: 'all 0.3s ease',
                                                                                                '&:hover': isRouteOptimized ? {
                                                                                                    transform: 'translateY(-4px)',
                                                                                                } : undefined,
                                                                                            }}
                                                                                        >
                                                                                            <Avatar sx={{
                                                                                                bgcolor: ((route.orderIndex === 1 || route.orderIndex === filteredRoutes.length) || (index === 0 || index === filteredRoutes.length - 1)) ? '#FFA500' : '#00b4d8',
                                                                                                color: 'white',
                                                                                                mr: 2
                                                                                            }}>
                                                                                                {route.orderIndex >= 1 ? route.orderIndex : index + 1}
                                                                                            </Avatar>
                                                                                            {/* New column for delivery type */}
                                                                                            <Tooltip title={`Tipo de entrega: ${clients.find(c => c.id === route.clientId)?.tipo_entrega || 'No especificado'}`}>
                                                                                                <IconButton sx={{ mr: 2 }}>
                                                                                                    {clients.find(c => c.id === route.clientId)?.tipo_entrega === 'Recolección'
                                                                                                        ? <RecoleccionIcon sx={{ color: 'green' }} />
                                                                                                        : <OllinIcon sx={{ color: '#00b4d8' }} />}
                                                                                                </IconButton>
                                                                                            </Tooltip>

                                                                                            {/* New column for status */}
                                                                                            <Tooltip title={`Estado: ${route.status || 'No especificado'}`}>
                                                                                                <IconButton sx={{ mr: 2 }}>
                                                                                                    {route.status === 'pending' && <PendingActions color="warning" />}
                                                                                                    {route.status === 'completed' && <CheckCircle color="success" />}
                                                                                                    {route.status === 'cancelled' && <Cancel color="error" />}
                                                                                                    {!route.status && <PendingActions color="disabled" />}
                                                                                                </IconButton>
                                                                                            </Tooltip>

                                                                                            {(() => {
                                                                                                // Buscar todas las reprogramaciones para este cliente
                                                                                                const reprogramaciones = pedidosPreprogramados
                                                                                                    ?.filter(pedido => {
                                                                                                        const matches = pedido.routeRreprogrammed?.clientId === route.clientId;
                                                                                                        // if (matches) {
                                                                                                        //     console.log('Pedido que coincide con el cliente:', pedido);
                                                                                                        // }
                                                                                                        return matches;
                                                                                                    })
                                                                                                    .map(pedido => ({
                                                                                                        ...pedido,
                                                                                                        fecha: pedido.originReprogrammedDate || pedido.destinyReprogrammedDate
                                                                                                    }))
                                                                                                    .sort((a, b) => {
                                                                                                        const timeA = a.fecha ? new Date(a.fecha).getTime() : 0;
                                                                                                        const timeB = b.fecha ? new Date(b.fecha).getTime() : 0;
                                                                                                        return timeB - timeA; // Orden descendente (más reciente primero)
                                                                                                    }) || [];

                                                                                                // console.log('Todas las reprogramaciones encontradas:', reprogramaciones);

                                                                                                if (reprogramaciones.length === 0) {
                                                                                                    // console.log('No se encontraron reprogramaciones para este cliente');
                                                                                                    return null;
                                                                                                }

                                                                                                const ultimaReprogramacion = reprogramaciones[0];
                                                                                                // console.log('Última reprogramación:', ultimaReprogramacion);
                                                                                                // console.log('ID de la ruta actual:', route.routeGroupId);
                                                                                                // console.log('ID de la ruta destino de la última reprogramación:', ultimaReprogramacion.destinyReprogrammedRouteGroupId);

                                                                                                const esRutaDestino = ultimaReprogramacion.destinyReprogrammedRouteGroupId === route.routeGroupId;
                                                                                                // console.log('¿Es la ruta destino?', esRutaDestino);

                                                                                                if (esRutaDestino) {
                                                                                                    // console.log('Mostrando indicador verde para la ruta destino');
                                                                                                    return <StatusDot color="#00FF00" style={{ marginRight: 8 }} />;
                                                                                                }

                                                                                                return null;
                                                                                            })()}

                                                                                            <FormControl fullWidth sx={{ mr: 2, width: '400px', height: '56px' }}>
                                                                                                <Autocomplete
                                                                                                    options={clients}
                                                                                                    value={clients.find(client => client.id === route.clientId) || null}
                                                                                                    onChange={(e, newValue) => {
                                                                                                        handleRouteChange(index, 'clientId', newValue?.id || '');
                                                                                                    }}
                                                                                                    filterOptions={(options, state) => {
                                                                                                        const inputValue = state.inputValue.toLowerCase();
                                                                                                        const selectedValue = clients.find(client => client.id === route.clientId)?.name.toLowerCase() || '';
                                                                                                        const searchText = state.inputValue ? inputValue : selectedValue;

                                                                                                        return options.filter(client =>
                                                                                                            (!combinedRoutes.some(r => r.clientId === client.id && r !== route)) &&
                                                                                                            client.name.toLowerCase().includes(searchText)
                                                                                                        );
                                                                                                    }}
                                                                                                    getOptionLabel={(option) => option.name || ''}
                                                                                                    popupIcon={null}
                                                                                                    renderInput={(params) => (
                                                                                                        <TextField
                                                                                                            {...params}
                                                                                                            label="Cliente"
                                                                                                            variant="outlined"
                                                                                                            InputLabelProps={{
                                                                                                                sx: { backgroundColor: 'white', padding: '0 4px', fontSize: '1rem', fontWeight: 'bold' }
                                                                                                            }}
                                                                                                        />
                                                                                                    )}
                                                                                                    noOptionsText="No se encontraron clientes"
                                                                                                />
                                                                                            </FormControl>
                                                                                            {route.clientId && (
                                                                                                (() => {
                                                                                                    const selectedClient = clients.find(client => client.id === route.clientId);
                                                                                                    if (!selectedClient) return null; // Asegúrate de que selectedClient esté definido

                                                                                                    return (
                                                                                                        <Box sx={{ mb: 1, mt: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                                                            <FormControl fullWidth sx={{ flex: 1, height: '56px' }}>
                                                                                                                <InputLabel
                                                                                                                    sx={{
                                                                                                                        backgroundColor: 'white',
                                                                                                                        padding: '0 4px',
                                                                                                                        fontSize: '1rem',
                                                                                                                        fontWeight: 'bold',
                                                                                                                    }}
                                                                                                                    shrink={!!selectedClient.canton} // Shrink if there is a value
                                                                                                                >
                                                                                                                    Cantón
                                                                                                                </InputLabel>

                                                                                                                <TextField
                                                                                                                    value={selectedClient.canton}
                                                                                                                    InputProps={{
                                                                                                                        readOnly: true, // Make it read-only if you don't want to allow editing
                                                                                                                        sx: {
                                                                                                                            borderRadius: '8px', // Aplicar borderRadius aquí
                                                                                                                        },
                                                                                                                    }}
                                                                                                                    sx={{
                                                                                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        fontSize: '0.9rem',
                                                                                                                        backgroundColor: '#f9f9f9',
                                                                                                                        height: '100%', // Asegurarse de que el TextField ocupe toda la altura
                                                                                                                    }}
                                                                                                                />
                                                                                                            </FormControl>
                                                                                                            <FormControl fullWidth sx={{ flex: 1, height: '56px' }}>
                                                                                                                <InputLabel
                                                                                                                    sx={{
                                                                                                                        backgroundColor: 'white',
                                                                                                                        padding: '0 4px',
                                                                                                                        fontSize: '1rem',
                                                                                                                        fontWeight: 'bold',
                                                                                                                    }}
                                                                                                                    shrink={!!selectedClient.distrito} // Shrink if there is a value
                                                                                                                >
                                                                                                                    Distrito
                                                                                                                </InputLabel>
                                                                                                                <TextField
                                                                                                                    value={selectedClient.distrito}
                                                                                                                    InputProps={{
                                                                                                                        readOnly: true, // Make it read-only if you don't want to allow editing
                                                                                                                        sx: {
                                                                                                                            borderRadius: '8px', // Aplicar borderRadius aquí
                                                                                                                        },
                                                                                                                    }}
                                                                                                                    sx={{
                                                                                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                                                                                                        fontSize: '0.9rem',
                                                                                                                        backgroundColor: '#f9f9f9',
                                                                                                                        height: '100%', // Asegurarse de que el TextField ocupe toda la altura
                                                                                                                    }}
                                                                                                                />
                                                                                                            </FormControl>
                                                                                                        </Box>
                                                                                                    );
                                                                                                })()
                                                                                            )}
                                                                                            <Tooltip title="Ver detalles">
                                                                                                <IconButton
                                                                                                    onClick={() => handleViewDetails(route, index)}
                                                                                                    sx={{
                                                                                                        color: '#00b4d8',
                                                                                                        '&:hover': { backgroundColor: 'rgba(0, 180, 216, 0.1)' },
                                                                                                    }}
                                                                                                >
                                                                                                    <VisibilityIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            <Tooltip title={currentRouteGroup?.unificationFlag ? "Dirigirse a la seccion de Editar Cliente en ruta unificada" : "Editar"}>
                                                                                                <IconButton
                                                                                                    onClick={() => {
                                                                                                        handleCreateOrEditOrder(currentRouteGroup!, route, index);
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        color: '#00b4d8',
                                                                                                        '&:hover': { backgroundColor: 'rgba(0, 180, 216, 0.1)' },
                                                                                                    }}
                                                                                                    disabled={!isRouteOptimized || currentRouteGroup?.unificationFlag}
                                                                                                >
                                                                                                    <EditIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            <Tooltip title={currentRouteGroup?.unificationFlag ? "No se puede Eliminar cliente en ruta unificada" : "Eliminar"}>
                                                                                                <span>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() => handleDeleteRoute(route.clientId)}
                                                                                                        sx={{ color: 'red' }}
                                                                                                        disabled={!isRouteOptimized || currentRouteGroup?.unificationFlag}
                                                                                                    >
                                                                                                        <DeleteIcon />
                                                                                                    </IconButton>
                                                                                                </span>
                                                                                            </Tooltip>
                                                                                            <Tooltip title="Preprogramar pedido">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        setSelectedRoute(route);
                                                                                                        setCalendarDialogOpen(true);
                                                                                                    }}
                                                                                                    sx={{ color: '#00b4d8' }}
                                                                                                    disabled={!isRouteOptimized || currentRouteGroup?.unificationFlag}
                                                                                                >
                                                                                                    <CalendarTodayIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        </Paper>
                                                                                    )}
                                                                                </Draggable>
                                                                            );
                                                                        })}
                                                                        {provided.placeholder}
                                                                    </Box>
                                                                )}
                                                            </Droppable>
                                                        )}
                                                    </DragDropContext>
                                                </Box>

                                                {/* Sección de pedidos reprogramados */}
                                                {showReprogrammedOrders && pedidosPreprogramados && pedidosPreprogramados.length > 0 && (
                                                    <Box sx={{ mt: 3, mb: 2 }}>
                                                        <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                            Pedidos Reprogramados
                                                        </Typography>
                                                        <Box sx={{
                                                            border: '2px dashed #ff4d4f',
                                                            borderRadius: '8px',
                                                            p: 2,
                                                            backgroundColor: '#fff2f0',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto',
                                                            '&::-webkit-scrollbar': {
                                                                width: '6px',
                                                            },
                                                            '&::-webkit-scrollbar-track': {
                                                                background: '#f1f1f1',
                                                                borderRadius: '4px',
                                                            },
                                                            '&::-webkit-scrollbar-thumb': {
                                                                background: '#ffa39e',
                                                                borderRadius: '4px',
                                                                '&:hover': {
                                                                    background: '#ff7875',
                                                                },
                                                            },
                                                        }}>
                                                            {pedidosPreprogramados
                                                                .filter(pedido => {
                                                                    if (!pedido.originReprogrammedDate) return false;
                                                                    const pedidoDate = pedido.originReprogrammedDate instanceof Date ?
                                                                        pedido.originReprogrammedDate :
                                                                        (pedido.originReprogrammedDate as any)?.toDate?.() || new Date(pedido.originReprogrammedDate);
                                                                    return areDatesEqual(pedidoDate, selectedDate || new Date());
                                                                })
                                                                .map((pedido, index) => {
                                                                    const client = pedido.routeRreprogrammed?.clientId ?
                                                                        clients.find(c => c.id === pedido.routeRreprogrammed?.clientId) : null;
                                                                    const pedidoDate = pedido.originReprogrammedDate ?
                                                                        (pedido.originReprogrammedDate instanceof Date ?
                                                                            pedido.originReprogrammedDate :
                                                                            (pedido.originReprogrammedDate as any)?.toDate?.() ||
                                                                            new Date(pedido.originReprogrammedDate)) : null;

                                                                    return (
                                                                        <Paper
                                                                            key={index}
                                                                            sx={{
                                                                                p: 2,
                                                                                mb: 1,
                                                                                backgroundColor: '#fff',
                                                                                borderLeft: '4px solid #ff4d4f',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                gap: 2,
                                                                                '&:hover': {
                                                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                                                },
                                                                                transition: 'all 0.2s ease-in-out'
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                                                                                <Avatar sx={{
                                                                                    bgcolor: '#ff4d4f',
                                                                                    color: 'white',
                                                                                    width: 32,
                                                                                    height: 32,
                                                                                    fontSize: '0.875rem'
                                                                                }}>
                                                                                    <InfoIcon fontSize="small" />
                                                                                </Avatar>
                                                                                <Box sx={{ flexGrow: 1 }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                            {client?.name || 'Cliente no encontrado'}
                                                                                        </Typography>
                                                                                        {/* <Tooltip title={client?.tipo_entrega === 'Recolección' ? 'Recolección' : 'Entrega'}>
                                                                                            <IconButton size="small" sx={{ p: 0.5 }}>
                                                                                                {client?.tipo_entrega === 'Recolección' ?
                                                                                                    <RecoleccionIcon sx={{ color: 'green', fontSize: '1rem' }} /> :
                                                                                                    <OllinIcon sx={{ color: '#00b4d8', fontSize: '1rem' }} />}
                                                                                            </IconButton>
                                                                                        </Tooltip> */}
                                                                                    </Box>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>

                                                                                        {client?.tipo_entrega && (
                                                                                            <Chip
                                                                                                icon={client.tipo_entrega === 'Recolección' ?
                                                                                                    <RecyclingIcon fontSize="small" /> :
                                                                                                    <LocalShipping fontSize="small" />}
                                                                                                label={client.tipo_entrega}
                                                                                                size="small"
                                                                                                color={client.tipo_entrega === 'Recolección' ? 'primary' : 'secondary'}
                                                                                                sx={{
                                                                                                    fontSize: '0.65rem',
                                                                                                    height: '20px',
                                                                                                    '& .MuiChip-label': { px: 0.75 }
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                        <Chip
                                                                                            icon={
                                                                                                pedido.routeRreprogrammed?.delivery_completed ?
                                                                                                    <CheckCircle fontSize="small" /> :
                                                                                                    <PendingActions fontSize="small" />
                                                                                            }
                                                                                            label={pedido.routeRreprogrammed?.delivery_completed ? 'Entregado' : 'Pendiente'}
                                                                                            size="small"
                                                                                            color={pedido.routeRreprogrammed?.delivery_completed ? 'success' : 'warning'}
                                                                                            sx={{
                                                                                                fontSize: '0.65rem',
                                                                                                height: '20px',
                                                                                                '& .MuiChip-label': { px: 0.75 },
                                                                                                '& .MuiChip-icon': {
                                                                                                    fontSize: '0.8rem',
                                                                                                    ml: 0.5
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                    {pedido.routeRreprogrammed?.clientId && (
                                                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                            <PlaceIcon fontSize="inherit" />
                                                                                            {client?.canton || 'Ubicación no disponible'}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            </Box>
                                                                            <Tooltip title="Ver detalles">
                                                                                <IconButton
                                                                                    onClick={() => handleViewDetails(pedido.routeRreprogrammed, index)}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        color: '#00b4d8',
                                                                                        '&:hover': { backgroundColor: 'rgba(0, 180, 216, 0.1)' },
                                                                                    }}
                                                                                >
                                                                                    <VisibilityIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Paper>
                                                                    );
                                                                })}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4} sx={{ height: { xs: '50vh', md: '100%' }, order: { xs: 1, md: 2 } }}>
                                            {/* <InfoSection elevation={3}>
                                                    <SectionTitle variant="h6">
                                                        <IconWrapper>
                                                            <CalendarToday />
                                                            Configuración de la Automatización
                                                        </IconWrapper>
                                                    </SectionTitle>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={3}>
                                                            <NameInput
                                                                initialName={currentRouteGroup?.name || ''}
                                                                onNameChange={handleNameChange}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={3}>
                                                            <StyledFormControl fullWidth>
                                                                <InputLabel
                                                                    sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                                                                >Tipo de Ruta</InputLabel>
                                                                <Select
                                                                    value={currentRouteGroup?.routeType || 'weekly'}
                                                                    onChange={(e) => {
                                                                        if (currentRouteGroup) {
                                                                            setCurrentRouteGroup({
                                                                                ...currentRouteGroup,
                                                                                routeType: e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'bimonthly'
                                                                            });
                                                                        }
                                                                    }}
                                                                >
                                                                    <MenuItem value="weekly">Semanal</MenuItem>
                                                                    <MenuItem value="biweekly">Bisemanal</MenuItem>
                                                                    <MenuItem value="monthly">Mensual</MenuItem>
                                                                    <MenuItem value="bimonthly">Bimensual</MenuItem>
                                                                </Select>
                                                            </StyledFormControl>
                                                        </Grid>
                                                        {(currentRouteGroup?.routeType === 'monthly' || currentRouteGroup?.routeType === 'bimonthly') && (
                                                            <>
                                                                <Grid item xs={12} md={3}>
                                                                    <StyledFormControl fullWidth>
                                                                        <InputLabel
                                                                            sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                                                                        >Día de la Semana</InputLabel>
                                                                        <Select
                                                                            value={currentRouteGroup?.dayOfWeek || 1}
                                                                            onChange={(e) => {
                                                                                if (currentRouteGroup) {
                                                                                    setCurrentRouteGroup({
                                                                                        ...currentRouteGroup,
                                                                                        dayOfWeek: e.target.value as number
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                                                                                <MenuItem key={day} value={day}>{['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'][day - 1]}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </StyledFormControl>
                                                                </Grid>
                                                                <Grid item xs={12} md={3}>
                                                                    <StyledFormControl fullWidth>
                                                                        <InputLabel
                                                                            sx={{ backgroundColor: 'white', padding: '0 4px' }} // Estilo agregado al InputLabel
                                                                        >Semana del Mes</InputLabel>
                                                                        <Select
                                                                            value={currentRouteGroup?.weekOfMonth || 1}
                                                                            onChange={(e) => {
                                                                                if (currentRouteGroup) {
                                                                                    setCurrentRouteGroup({
                                                                                        ...currentRouteGroup,
                                                                                        weekOfMonth: e.target.value as number
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            {[1, 2, 3, 4, 5].map((week) => (
                                                                                <MenuItem key={week} value={week}>{week}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </StyledFormControl>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {(currentRouteGroup?.routeType === 'weekly' || currentRouteGroup?.routeType === 'biweekly') && (
                                                            <Grid item xs={12} md={3}>
                                                                <StyledFormControl fullWidth>
                                                                    <DatePicker
                                                                        selected={currentRouteGroup?.createdAt}
                                                                        onChange={(date: Date | null) => {
                                                                            if (currentRouteGroup && date) {
                                                                                setCurrentRouteGroup({
                                                                                    ...currentRouteGroup,
                                                                                    createdAt: date
                                                                                });
                                                                            }
                                                                        }}
                                                                        customInput={
                                                                            <TextField
                                                                                fullWidth
                                                                                label="Fecha de inicio"
                                                                                InputProps={{
                                                                                    startAdornment: (
                                                                                        <Today style={{ color: '#00b4d8', marginRight: '8px' }} />
                                                                                    ),
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                </StyledFormControl>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </InfoSection> */}
                                            <Box sx={{
                                                height: '100%',
                                                position: 'sticky',
                                                top: 24
                                            }}>
                                                {renderMapFilter()}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'right', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
                            <Button onClick={handleSaveRouteGroup} variant="contained"
                                sx={{
                                    backgroundColor: '#87CEEB',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#5CACEE',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    },
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 1,
                                }}
                                // disabled={!isSaveButtonEnabled} // Disable button based on the new state
                                // disabled={true}
                                ref={saveButtonRef} // Assign the ref here
                            >Guardar</Button>
                            <Button onClick={handleCloseEditDialog} variant="outlined"
                                sx={{
                                    color: '#87CEEB',
                                    borderColor: '#87CEEB',
                                    '&:hover': {
                                        backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                        borderColor: '#5CACEE',
                                    },
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 1,
                                    mr: 2,
                                }}>Cancelar</Button>
                        </DialogActions>
                    </Dialog>
                </motion.div>
            )
            }
        </AnimatePresence >

    );

    const handleViewDetails = useCallback((route: Route, index: number) => {
        console.log("handleViewDetails called with route:", route);
        if (!currentRouteGroup) {
            console.log("currentRouteGroup is null or undefined");
            return;
        }

        setSelectedRoute(route);
        setSelectedMarker(null);
        setHoveredMarker(null);
        setCurrentRouteIndex(index);
        console.log("Opening view dialog");
        setViewDialogOpen(true);
        console.log("Dialog state set to open");
    }, [currentRouteGroup]);

    const handleCloseViewDialog = useCallback(() => {
        setSelectedMarker(null);
        setHoveredMarker(null);
        setViewDialogOpen(false);
        setSelectedRoute(null);
    }, []);

    // const handleOrderCreated = (newOrder: Order) => {
    //     if (currentRoute) {
    //         setCurrentRoute({
    //             ...currentRoute,
    //             order: newOrder
    //         });
    //     }
    //     setIsOrderModalOpen(false);
    // };

    const handleCreateOrEditOrder = async (routeGroup: RouteGroup, route: Route, index: number) => {
        try {
            setSelectedRoute(route);
            setCurrentRouteIndex(index);
            // 1. Get first routeUnion doc
            const routesUnionRef = collection(db, `routeGroups/${routeGroup.id}/routesUnion`);
            const routesUnionSnapshot = await getDocs(query(routesUnionRef));

            if (routesUnionSnapshot.empty) {
                throw new Error('No routeUnion documents found');
            }

            const routeUnionDoc = routesUnionSnapshot.docs[0];
            const routesUnionData = routeUnionDoc.data();

            // 2. Check if route exists
            const currentRoute = routesUnionData.routes?.find((r: Route) => r.id === route.id);

            // 3. Set editOrder state
            setEditOrder({
                routeGroupId: routeGroup.id || '',
                routeUnionId: routeUnionDoc.id,
                route: {
                    id: route.id,
                    clientId: route.clientId || '',
                    status: route.status || 'pending',
                    order: route.order || { items: [] },
                    tipo_pedido: 'Automatizado',
                    fecha_pedido: currentRoute?.fecha_pedido || new Date(),
                    routeGroupId: routeGroup.id || '',
                    note_admin: currentRoute?.note_admin || '',
                    note_driver: currentRoute?.note_driver || '',
                    deliveryFileUrl: currentRoute?.deliveryFileUrl || '',
                    container_values: currentRoute?.container_values,
                    lenght_container_values: currentRoute?.container_values?.length
                }
            });

            // 4. Update route data
            const updatedRoute: Route = {
                ...route,
                id: route.id,
                clientId: route.clientId || '',
                status: route.status || 'pending',
                order: route.order || { items: [] },
                tipo_pedido: "Automatizado",
                fecha_pedido: currentRoute?.fecha_pedido || new Date(),
                note_admin: currentRoute?.note_admin || '',
                note_driver: currentRoute?.note_driver || '',
                routeGroupId: route.routeGroupId || '',
                deliveryFileUrl: currentRoute?.deliveryFileUrl || '',
                container_values: currentRoute?.container_values,
                lenght_container_values: currentRoute?.container_values?.length
            };

            // // 5. Update Firestore
            // const updatedRoutes = currentRoute
            //   ? routesUnionData.routes.map((r: Route) => r.id === route.id ? updatedRoute : r)
            //   : [...routesUnionData.routesUnion, updatedRoute];

            // await updateDoc(doc(db, `routeGroups/${routeGroup.id}/routesUnion`, routeUnionDoc.id), {
            //   routes: updatedRoutes
            // });

            // 6. Set UI states
            setCurrentRouteGroup(routeGroup);
            setCurrentRoute(updatedRoute);
            setIsOrderModalOpen(true);
        } catch (error) {
            console.error("Error processing route:", error);
            toast.error('Error al procesar la ruta');
        }
    };


    const handleOrderCreated = async (newOrder: Order, noteAdmin?: string, noteDriver?: string, deliveryFileUrl?: string, container_values?: string[], lenght_container_values?: string) => {
        console.log('Starting handleOrderCreated with:', {
            hasCurrentRouteGroup: !!currentRouteGroup,
            hasCurrentRoute: !!currentRoute,
            hasEditOrder: !!editOrder
        });

        if (currentRouteGroup && currentRoute && editOrder) {
            try {
                console.log('Fetching routesUnion document...');
                // First get current routes from routesUnion document
                const routesUnionRef = doc(db, `routeGroups/${currentRouteGroup.id}/routesUnion`, editOrder.routeUnionId);
                const routesUnionDoc = await getDoc(routesUnionRef);
                const routesUnionData = routesUnionDoc.data();

                if (!routesUnionData) {
                    throw new Error('No routes union data found');
                }

                // Update the specific route with new order and notes
                const updatedRoutes = routesUnionData.routes.map((r: Route) => {
                    if (r.clientId === currentRoute.clientId) {
                        return {
                            ...r,
                            order: newOrder,
                            status: 'pending',
                            note_admin: noteAdmin ?? r.note_admin ?? '',
                            note_driver: noteDriver ?? r.note_driver ?? '',
                            deliveryFileUrl: deliveryFileUrl ?? r.deliveryFileUrl ?? '',
                            container_values: container_values ?? r.container_values ?? [],
                            lenght_container_values: lenght_container_values ?? r.lenght_container_values ?? '',
                        };
                    }
                    return r;
                });
                // console.log('Routes updated:', updatedRoutes);
                // Update local state
                setCurrentRouteGroup({
                    ...currentRouteGroup,
                    routes: updatedRoutes
                });

                // Update Firestore with all routes including notes
                await updateDoc(routesUnionRef, {
                    routes: updatedRoutes
                });

                // Update inventory items
                const updatedInventoryItems = [...inventoryItems];
                for (const item of newOrder.items) {
                    const inventoryItem = updatedInventoryItems.find(i => i.id === item.inventoryItemId);
                    if (inventoryItem) {
                        inventoryItem.quantity -= item.quantity;
                    }
                }
                setInventoryItems(updatedInventoryItems);
                setCombinedRoutes(updatedRoutes);

                fetchRouteGroups();

                // setCurrentRoute(null);
                // setEditOrder(null);

                toast.success('Orden y notas actualizadas exitosamente');
            } catch (error) {
                console.error("Error updating route group with new order and notes: ", error);
                toast.error('Error al actualizar el grupo de rutas');
            } finally {
                setCurrentRoute(null);
                setEditOrder(null);
                window.location.reload();
                setIsOrderModalOpen(false);
            }
        }
    };


    const handleOpenCalendarDialog = (routeGroup: RouteGroup) => {
        setSelectedRouteGroup(routeGroup);
        // console.log('Selected RouteGroup:', routeGroup);
        setCalendarDialogOpen(true);
    };

    const handleCloseCalendarDialog = () => {
        setCalendarDialogOpen(false);
        setSelectedRoute(null);
    };

    const renderDayCellsWithRoutes = () => {
        const days = getDaysInMonth(currentDate);
        const totalCells = 42;
        const weeks: React.ReactNode[] = [];
        let week: React.ReactNode[] = [];
        let emptyWeek = true;

        for (let i = 0; i < totalCells; i++) {
            const date = i < days.length ? days[i] : null;
            const routeGroups = date ? getRoutesForDay(date) : [];
            const isWeekend = date ? (date.getDay() === 0 || date.getDay() === 6) : false;
            const dateKey = date ? date.toISOString().split('T')[0] : '';

            // Check if any route group has a yellow border (with-border or with-full-border class)
            const hasYellowBorderRoute = routeGroups.some(routeGroup => {
                const dateMatch = routeGroup.routesUnion?.some(routeItem => {
                    const routeDate = routeItem?.[1];
                    const routeDateObj = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                    return areDatesEqual(routeDateObj, date);
                });
                const hasRoutes = dateMatch && Boolean(routeGroup.routesUnion?.[0]?.[0]?.length > 0);
                return routeGroup.unificationFlag || hasRoutes;
            });

            const cell = (
                <Grid item xs key={i} style={{
                    width: 'calc(100% / 7)',
                    maxWidth: 'calc(100% / 7)',
                    flexBasis: 'calc(100% / 7)',
                    minHeight: '150px'
                }}>
                    <DayCell
                        isCurrentDay={date ? isCurrentDay(date) : false}
                        hasRoutes={hasYellowBorderRoute}
                        isWeekend={isWeekend}
                        isCurrentMonth={date ? isCurrentMonth(date) : false}
                        style={{
                            width: '100%',
                            height: '100%',
                            transition: 'all 0.2s ease-in-out'
                        }}
                        className="hoverable-day-cell"
                    >
                        {date && (
                            <>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <DayNumber isCurrentDay={isCurrentDay(date)}>{date.getDate()}</DayNumber>
                                    {date > new Date(new Date().setHours(0, 0, 0, 0)) && routeGroups.some(routeGroup => routeGroup.unificationFlag === true) && (
                                        <Switch
                                            checked={switchStates[dateKey] || false}
                                            onChange={() => handleSwitchChange(dateKey)}
                                            color="primary"
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-thumb': {
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: switchStates[dateKey] ? '#FFD700' : 'default',
                                                },
                                                '& .MuiSwitch-track': {
                                                    height: 16,
                                                    backgroundColor: switchStates[dateKey] ? 'rgba(255, 215, 0, 0.3)' : undefined,
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                                <RouteList>
                                    {routeGroups
                                        .filter((routeGroup: RouteGroup) => {
                                            // Skip if date is before today
                                            if (date && date < new Date(new Date().setHours(0, 0, 0, 0))) {
                                                return false;
                                            }
                                            if (switchStates[dateKey]) {
                                                return routeGroup.unificationFlag === true;
                                            }
                                            return routeGroup.unificationFlag === undefined || routeGroup.unificationFlag === false;
                                        })
                                        .map((routeGroup: RouteGroup) => {
                                            const dateMatch = routeGroup.routesUnion?.some(routeItem => {
                                                const routeDate = routeItem?.[1];
                                                const routeDateObj = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                                                return areDatesEqual(routeDateObj, date);
                                            });

                                            const hasRoutes = dateMatch && Boolean(routeGroup.routesUnion?.[0]?.[0]?.length > 0);
                                            const hasRoutesUnion = hasRoutes !== undefined ? hasRoutes : false;

                                            if (!routeGroup.unificationFlag && !hasRoutesUnion) return null;

                                            return (
                                                <RouteItem
                                                    key={routeGroup.id}
                                                    onClick={() => {
                                                        handleEditRouteReprogramar(routeGroup, date);
                                                    }}
                                                    className={routeGroup.unificationFlag ? 'with-full-border' : 'with-border'}
                                                >
                                                    <Tooltip title={routeGroup.name} arrow>
                                                        <Typography variant="caption" noWrap>
                                                            {routeGroup.name}
                                                        </Typography>
                                                    </Tooltip>
                                                </RouteItem>
                                            );
                                        })}
                                </RouteList>
                            </>
                        )}
                    </DayCell>
                </Grid>
            );

            week.push(cell);
            emptyWeek = false;

            if (week.length === 7) {
                if (!emptyWeek) {
                    weeks.push(
                        <Grid container spacing={1} key={`week-${weeks.length}`} style={{ marginBottom: '8px' }}>
                            {week}
                        </Grid>
                    );
                }
                week = [];
                emptyWeek = true;
            }
        }

        return weeks;
    };

    // Renderizar el diálogo del calendario
    const renderCalendarDialog = () => (
        <Dialog open={calendarDialogOpen} onClose={handleCloseCalendarDialog} fullWidth maxWidth="lg">
            <DialogTitle sx={{ bgcolor: '#00b4d8', color: 'white' }}>
                <MonthNavigationDialog>
                    <NavButton onClick={handlePrevMonth} startIcon={<ChevronLeft />}>
                        ANTERIOR
                    </NavButton>
                    <MonthYearLabel sx={{ color: 'white' }} >
                        {currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' }).toUpperCase()}
                    </MonthYearLabel>
                    <NavButton onClick={handleNextMonth} endIcon={<ChevronRight />}>
                        SIGUIENTE
                    </NavButton>
                </MonthNavigationDialog>
            </DialogTitle>
            <br />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                            {['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'].map((day) => (
                                <Grid item xs key={day}>
                                    <WeekdayLabel>
                                        {day}
                                    </WeekdayLabel>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    {/* Renderiza las celdas del calendario con rutas */}
                    {renderDayCellsWithRoutes()}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
                <Button onClick={handleCloseCalendarDialog} variant="outlined"
                    sx={{
                        color: '#87CEEB',
                        borderColor: '#87CEEB',
                        '&:hover': {
                            backgroundColor: 'rgba(135, 206, 235, 0.1)',
                            borderColor: '#5CACEE',
                        },
                        fontWeight: 'bold',
                        px: 4,
                        py: 1,
                    }}
                >Cerrar</Button>
            </DialogActions>
        </Dialog>
    );

    {/* Modificar el contenido del popup */ }
    {
        showNotification && (
            <>
                <DarkOverlay />
                <NotificationPopup>
                    <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
                    <Typography
                        sx={{
                            color: '#1a1a1a',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Actualización exitosa
                    </Typography>
                    <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
                </NotificationPopup>
            </>
        )
    }
    {
        showNotificationLoading && (
            <>
                <DarkOverlay />
                <NotificationPopup>
                    <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
                    <Typography
                        sx={{
                            color: '#1a1a1a',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Cargando...
                    </Typography>
                    <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
                </NotificationPopup>
            </>
        )
    }

    return (
        <CalendarContainer sx={{ minHeight: 'calc(100vh - 100px)' }}> {/* Adjust 100px based on header/footer */}
            <MonthNavigation>
                <NavButton onClick={handlePrevMonth} startIcon={<ChevronLeft />}>
                    ANTERIOR
                </NavButton>
                <MonthYearLabel>
                    {currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' }).toUpperCase()}
                </MonthYearLabel>
                <NavButton onClick={handleNextMonth} endIcon={<ChevronRight />}>
                    SIGUIENTE
                </NavButton>
            </MonthNavigation>
            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                {['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'].map((day) => (
                    <Grid item xs key={day}>
                        <WeekdayLabel>
                            {day}
                        </WeekdayLabel>
                    </Grid>
                ))}
            </Grid>
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="calc(100vh - 100px)"
                    width="100%"
                >
                    <CircularProgress size={60} /> {/* Increased size for better visibility */}
                </Box>
            ) : (
                <>
                    {renderDayCells()}
                    {renderEditDialog()}
                    {/* Renderizar el diálogo del calendario */}
                    {renderCalendarDialog()}
                    {showReprogramConfirm && (
                        <Dialog
                            open={showReprogramConfirm}
                            onClose={handleCancelReprogram}
                            aria-labelledby="reprogram-confirm-dialog"
                            maxWidth="sm"
                            fullWidth
                            PaperProps={{
                                sx: (theme) => ({
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                                    border: 'none',
                                    '& .MuiDialogTitle-root': {
                                        bgcolor: '#00b4d8',
                                        color: 'white',
                                        py: 2,
                                        px: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing(1.5),
                                        typography: 'subtitle1',
                                        fontWeight: 600,
                                        '& svg': {
                                            fontSize: 24,
                                            mr: 1,
                                            color: 'white'
                                        }
                                    },
                                    '& .MuiDialogContent-root': {
                                        py: 3,
                                        px: 4,
                                        bgcolor: 'background.paper',
                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#f1f1f1',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: '#888',
                                            borderRadius: '3px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: '#555',
                                        },
                                    },
                                    '& .MuiDialogActions-root': {
                                        px: 3,
                                        py: 2.5,
                                        bgcolor: 'rgba(135, 206, 235, 0.1)',
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        justifyContent: 'center',
                                        gap: 2
                                    }
                                })
                            }}
                        >
                            <DialogTitle>
                                <CalendarTodayIcon />
                                Confirmar Reprogramación
                            </DialogTitle>
                            <DialogContent>
                                <Box textAlign="center" py={1}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            p: 2,
                                            mb: 3,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.light',
                                            color: 'primary.contrastText',
                                            boxShadow: (theme) => `0 4px 20px 0 ${theme.palette.primary.light}40`
                                        }}
                                    >
                                        <CalendarTodayIcon sx={{ fontSize: 40 }} />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 700,
                                            color: 'primary.main',
                                            mb: 3,
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        ¿Reprogramar pedido?
                                    </Typography>


                                    <Card
                                        variant="outlined"
                                        sx={{
                                            mb: 3,
                                            textAlign: 'left',
                                            borderColor: 'divider',
                                            bgcolor: 'grey.50',
                                            boxShadow: 'none',
                                            border: '1px solid'
                                        }}
                                    >
                                        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: 'primary.main',
                                                    borderRadius: '50%',
                                                    mr: 1.5
                                                }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                    <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Cliente:</Box>
                                                    {reprogramData.clientName || 'No especificado'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: 'primary.main',
                                                    borderRadius: '50%',
                                                    mr: 1.5
                                                }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                    <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Nueva ruta:</Box>
                                                    {reprogramData.routeGroup?.name || 'No especificada'}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Order Items Section */}
                                    <Box sx={{ mb: 3, textAlign: 'left' }}>
                                        <Typography variant="subtitle2" sx={{
                                            color: 'text.secondary',
                                            mb: 1.5,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <ShoppingCartIcon fontSize="small" />
                                            Artículos del pedido
                                        </Typography>
                                        <Box sx={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1,
                                            mb: 2
                                        }}>
                                            {(() => {
                                                console.log('Current Route:', currentRoute);
                                                console.log('Inventory Items:', inventoryItems);

                                                // Check if currentRoute is null or undefined
                                                if (!currentRoute) {
                                                    return (
                                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                                            Cargando información del pedido...
                                                        </Typography>
                                                    );
                                                }

                                                // Check if order items exist and is an array
                                                if (!currentRoute.order?.items || !Array.isArray(currentRoute.order.items)) {
                                                    return (
                                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                                            No hay información de artículos disponible
                                                        </Typography>
                                                    );
                                                }

                                                console.log('Order Items:', currentRoute.order.items);

                                                if (currentRoute.order.items.length === 0) {
                                                    return (
                                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                                            No hay artículos en este pedido
                                                        </Typography>
                                                    );
                                                }

                                                return currentRoute.order.items.map((item, index) => {
                                                    if (!item) return null;
                                                    console.log('Processing item:', item);
                                                    const inventoryItem = inventoryItems.find(i => i.id === item.inventoryItemId);
                                                    console.log('Found inventory item:', inventoryItem);
                                                    if (!inventoryItem) {
                                                        console.log('Inventory item not found for ID:', item.inventoryItemId);
                                                        return null; // Skip if inventory item not found
                                                    }
                                                    return (
                                                        <Box key={index} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: 1.5,
                                                            borderRadius: 1,
                                                            bgcolor: 'background.paper',
                                                            '&:not(:last-child)': {
                                                                mb: 1,
                                                                borderBottom: '1px solid',
                                                                borderColor: 'divider',
                                                                pb: 1.5
                                                            }
                                                        }}>
                                                            <Avatar
                                                                src={inventoryItem?.imageUrl}
                                                                alt={inventoryItem?.name}
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    mr: 2,
                                                                    bgcolor: 'grey.200',
                                                                    '& img': {
                                                                        objectFit: 'contain',
                                                                        p: 0.5
                                                                    }
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{
                                                                    fontWeight: 600,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    mb: 0.5
                                                                }}>
                                                                    {inventoryItem?.name || `Artículo (ID: ${item.inventoryItemId})`}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Cantidad: {item.quantity}
                                                                    </Typography>
                                                                    {inventoryItem?.price && (
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 600,
                                                                            color: 'primary.main',
                                                                            fontFamily: 'monospace',
                                                                            letterSpacing: '0.5px'
                                                                        }}>
                                                                            ₡ {(inventoryItem.price * item.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',')}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                });
                                            })()}
                                        </Box>
                                    </Box>

                                    {/* Order Total */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        bgcolor: 'primary.light',
                                        p: 2,
                                        borderRadius: 1,
                                        mb: 3,
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}>
                                        <Typography variant="subtitle1" sx={{
                                            fontWeight: 600,
                                            color: 'primary.contrastText',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            {/* <AttachMoneyIcon fontSize="small" /> */}
                                            Total del pedido:
                                        </Typography>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 700,
                                            color: 'primary.contrastText',
                                            fontSize: '1.2rem',
                                            fontFamily: 'monospace',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {(() => {
                                                if (!currentRoute?.order?.items?.length) return '₡ 0,00';
                                                const total = currentRoute.order.items.reduce((sum, item) => {
                                                    const inventoryItem = inventoryItems.find(i => i.id === item.inventoryItemId);
                                                    return sum + (inventoryItem?.price || 0) * (item.quantity || 0);
                                                }, 0);
                                                // Format number with spaces as thousand separators and comma as decimal separator
                                                return `₡ ${total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',')}`;
                                            })()}
                                        </Typography>
                                    </Box>

                                    <Alert
                                        severity="info"
                                        icon={<PriorityHighIcon sx={{ color: '#0288d1' }} />}
                                        sx={{
                                            textAlign: 'left',
                                            bgcolor: 'rgba(3, 169, 244, 0.15)',
                                            color: '#01579b',
                                            '& .MuiAlert-icon': {
                                                color: '#0288d1',
                                                alignItems: 'center',
                                                opacity: 0.9
                                            },
                                            '& .MuiAlert-message': {
                                                py: 1,
                                                color: '#01579b'
                                            },
                                            border: '1px solid',
                                            borderColor: 'rgba(2, 136, 209, 0.3)',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#01579b' }}>
                                            Esta acción no se puede deshacer. Se moverá el pedido a la nueva fecha seleccionada.
                                        </Typography>
                                    </Alert>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
                                <Button
                                    onClick={handleConfirmReprogram}
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<CheckCircle />}
                                    sx={{
                                        backgroundColor: '#87CEEB',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#5CACEE',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        fontWeight: 'bold',
                                        px: 4,
                                        py: 1,
                                    }}
                                >
                                    Reprogramar
                                </Button>
                                <Button
                                    onClick={handleCancelReprogram}
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<CloseIcon />}
                                    sx={{
                                        color: '#87CEEB',
                                        borderColor: '#87CEEB',
                                        '&:hover': {
                                            backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                            borderColor: '#5CACEE',
                                        },
                                        fontWeight: 'bold',
                                        px: 4,
                                        py: 1,
                                    }}
                                >
                                    Cancelar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    <OrderCreationModalAutomatic
                        open={isOrderModalOpen}
                        onClose={() => setIsOrderModalOpen(false)}
                        inventoryItems={inventoryItems}
                        existingOrder={currentRoute?.order}
                        onOrderCreated={handleOrderCreated}
                        currentRouteGroup={currentRouteGroup || undefined} // Añadido
                        currentRoute={selectedRoute || undefined} // Añadido
                        onRouteGroupUpdate={handleRouteGroupUpdate || undefined} // Añadido
                    />
                    <SuccessModal
                        open={successModalOpen}
                        onClose={() => setSuccessModalOpen(false)}
                    >
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <AnimatedCheckIcon viewBox="0 0 52 52">
                                <AnimatedCircle cx="26" cy="26" r="25" />
                                <path fill="none" stroke="green" strokeWidth="3" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </AnimatedCheckIcon>
                            <Typography variant="h6">Cambios guardados exitosamente</Typography>
                        </Box>
                    </SuccessModal>
                    <Dialog
                        open={viewDialogOpen}
                        onClose={handleCloseViewDialog}
                        fullWidth
                        maxWidth="xl"
                        PaperProps={{
                            sx: {
                                borderRadius: '8px',
                                height: 'calc(100% - 64px)',
                                maxHeight: 'none',
                            },
                        }}
                    >
                        <DialogTitle
                            sx={{
                                backgroundColor: '#87CEEB',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.5rem',
                                py: 2,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                padding: '32px',
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: currentRouteIndex === 0 || (combinedRoutes && currentRouteIndex === combinedRoutes.length - 1) ? '#FFA500' : '#00b4d8',
                                    color: 'white',
                                    width: 40,
                                    height: 40,
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                }}
                            >
                                {currentRouteIndex + 1}
                            </Avatar>
                        </DialogTitle>
                        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0, position: 'relative' }}>
                            {selectedRoute && (
                                <>
                                    <Box sx={{ display: 'flex', height: '100%' }}>
                                        <Box sx={{ width: '25%', p: 3, borderRight: '1px solid #e0e0e0' }}>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Imagen del cliente</Typography>
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                border: '2px dashed #87CEEB',
                                                borderRadius: 2,
                                                mb: 3,
                                            }}>
                                                {(() => {
                                                    const client = clients?.find(c => c.id === selectedRoute.clientId);
                                                    return client?.imageUrl ? (
                                                        <img src={client.imageUrl} alt="Client" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                    ) : <Typography variant="body1" color="textSecondary">No hay imagen del cliente disponible</Typography>;
                                                })()}
                                            </Box>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Nota Admin del Pedido</Typography>
                                            <Box sx={{ mb: 3, width: '100%' }}>
                                                <Typography sx={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                    {selectedRoute.note_admin || 'No hay Nota Admin del Pedido'}
                                                </Typography>
                                            </Box>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Nota del Transportista</Typography>
                                            <Box sx={{ mb: 3, width: '100%' }}>
                                                <Typography sx={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                    {selectedRoute.note_driver || 'No hay Nota del Transportista'}
                                                </Typography>
                                            </Box>
                                            {/* agrega los datos correspondientes a Route.container_values como los kilos de cada contenedor
                                            estos datos de Route.container_values solo se visualizan si Route.delivery_completed es true */}

                                            {selectedRoute.container_values && selectedRoute.container_values.length > 0 && (
                                                <>
                                                    <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>
                                                        Contenedores (Kilos)
                                                    </Typography>
                                                    <Box sx={{ mb: 3, width: '100%' }}>
                                                        {selectedRoute.container_values.map((container, index) => (
                                                            <Box key={index} sx={{ mb: 1 }}>
                                                                <Typography>
                                                                    Contenedor {index + 1}: {container} kg
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </>
                                            )}
                                            {/* muestrala imagen que esta en Route.deliveryFileUrl */}
                                            {selectedRoute.delivery_completed && selectedRoute.deliveryFileUrl && (
                                                <>
                                                    <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>
                                                        Imagen de Entrega
                                                    </Typography>
                                                    <Box sx={{
                                                        mb: 3,
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        border: '2px dashed #87CEEB',
                                                        borderRadius: 2,
                                                        padding: 2,
                                                        minHeight: '200px'
                                                    }}>
                                                        {selectedRoute.deliveryFileUrl ? (
                                                            <img
                                                                src={selectedRoute.deliveryFileUrl}
                                                                alt="Imagen de entrega"
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    maxHeight: '300px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body1" color="textSecondary">
                                                                No hay imagen de entrega disponible
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        <Box sx={{ width: '25%', p: 3, borderRight: '1px solid #e0e0e0' }}>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Información Personal</Typography>
                                            <Box sx={{ mb: 2 }}>                                 <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.name}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Teléfono</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.phoneNumber}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.email}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Cédula Nacional</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.cedula_nacional}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Tipo de Documento</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.tipo_documento}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Tipo de Cliente</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.tipo_client}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Tipo de Compostera</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.tipo_compostera}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="textSecondary">Tipo de Entrega</Typography>
                                                {clients.find(c => c.id === selectedRoute.clientId)?.tipo_entrega === 'Productos' ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <ShoppingCartIcon sx={{ color: 'rgb(71, 219, 226)', mr: 1 }} />
                                                        <Typography>Productos</Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <RecoleccionIcon sx={{ color: 'green', mr: 1 }} />
                                                        <Typography>Recolección</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ width: '25%', p: 3, borderRight: '1px solid #e0e0e0' }}>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Ubicación</Typography>

                                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                <IconButton
                                                    onClick={() => window.open(clients.find(c => c.id === selectedRoute.clientId)?.googleMapsLink, '_blank')}
                                                    disabled={!clients.find(c => c.id === selectedRoute.clientId)?.googleMapsLink}
                                                    sx={{
                                                        color: '#0078D4',
                                                        backgroundColor: 'white',
                                                        padding: '12px',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 120, 212, 0.15)',
                                                            transform: 'scale(1.1)',
                                                            boxShadow: '0 4px 8px rgba(0, 120, 212, 0.2)'
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                            color: 'rgba(0, 0, 0, 0.26)'
                                                        }
                                                    }}
                                                >
                                                    <GoogleIcon style={{ fontSize: 32 }} />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => window.open(clients.find(c => c.id === selectedRoute.clientId)?.wazeLink, '_blank')}
                                                    disabled={!clients.find(c => c.id === selectedRoute.clientId)?.wazeLink}
                                                    sx={{
                                                        mr: '8px', // Ajuste de margen derecho
                                                        backgroundColor: 'white',
                                                        color: '#33ccff',
                                                        padding: '12px',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 120, 212, 0.15)',
                                                            transform: 'scale(1.1)',
                                                            boxShadow: '0 4px 8px rgba(0, 120, 212, 0.2)'
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#e0e0e0',
                                                            color: '#9e9e9e',
                                                        }
                                                    }}
                                                >
                                                    <FaWaze style={{ fontSize: 32 }} />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Provincia</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.provincia}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Cantón</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.canton}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Distrito</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.distrito}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Municipalidad</Typography>
                                                <Typography>{clients.find(c => c.id === selectedRoute.clientId)?.municipalidades}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Señas</Typography>
                                                <Typography sx={{ wordBreak: "break-word" }}>{clients.find(c => c.id === selectedRoute.clientId)?.senas}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Nota del cliente</Typography>
                                                <Typography sx={{ wordBreak: "break-word" }}>{clients.find(c => c.id === selectedRoute.clientId)?.nota}</Typography>
                                            </Box>
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" color="textSecondary">Nota Admin</Typography>
                                                <Typography sx={{ wordBreak: "break-word" }}>{clients.find(c => c.id === selectedRoute.clientId)?.nota_admin}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ width: '25%', p: 3, display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Detalles del Pedido</Typography>
                                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                                {selectedRoute.order?.items.filter(item => item.quantity > 0).map(item => {
                                                    const product = inventoryItems.find(p => p.id === item.inventoryItemId);

                                                    return (
                                                        <Box key={item.inventoryItemId} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                onMouseEnter={() => setPreviewItem(item.inventoryItemId)}
                                                                onMouseLeave={() => setPreviewItem(null)}
                                                                sx={{
                                                                    position: 'relative',
                                                                    '&:hover .preview-image': {
                                                                        opacity: 1,
                                                                        visibility: 'visible'
                                                                    }
                                                                }}
                                                            >
                                                                <Avatar
                                                                    src={product?.imageUrl}
                                                                    alt={product?.name || 'Product'}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                                <Box
                                                                    className="preview-image"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        left: '100%',
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        marginLeft: '12px',
                                                                        marginTop: '77px',
                                                                        width: '200px',
                                                                        height: '200px',
                                                                        opacity: 0,
                                                                        visibility: 'hidden',
                                                                        transition: 'all 0.2s ease-in-out',
                                                                        zIndex: 1000,
                                                                        boxShadow: 3,
                                                                        borderRadius: '8px',
                                                                        overflow: 'hidden',
                                                                        '& img': {
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={product?.imageUrl}
                                                                        alt={product?.name || 'Preview'}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ ml: 2 }}>
                                                                <Typography variant="subtitle1">{product?.name}</Typography>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Cantidad: {item.quantity}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                            <Box sx={{ mt: 3 }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: '#87CEEB', fontWeight: 'bold' }}>Mascotas</Typography>
                                                <Grid container spacing={2}>
                                                    {['cant_gato', 'cant_perro_mini', 'cant_perro_pequeno', 'cant_perro_mediano', 'cant_perro_grande', 'cant_perro_gigante']
                                                        .filter(field => {
                                                            const client = clients.find(c => c.id === selectedRoute.clientId);
                                                            return client && parseInt(client[field as keyof Client] as string, 10) > 0;
                                                        })
                                                        .map((field) => {
                                                            const client = clients.find(c => c.id === selectedRoute.clientId);
                                                            return (
                                                                <Grid item xs={6} key={field}>
                                                                    <Box display="flex" alignItems="center">
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/${field}.png`}
                                                                            alt={columnNames[field as keyof Client]}
                                                                            style={{ width: '40px', height: '40px', marginRight: '8px' }}
                                                                        />
                                                                        <Typography variant="body2">{columnNames[field as keyof Client]}: {client?.[field as keyof Client]}</Typography>
                                                                    </Box>
                                                                </Grid>
                                                            );
                                                        })}
                                                </Grid>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'right', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
                            <Button
                                onClick={handleCloseViewDialog}
                                variant="outlined"
                                sx={{
                                    color: '#87CEEB',
                                    borderColor: '#87CEEB',
                                    '&:hover': {
                                        backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                        borderColor: '#5CACEE',
                                    },
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 1,
                                }}
                            >
                                Cerrar
                            </Button>
                        </DialogActions>
                    </Dialog>

                </>
            )}
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
                fullScreen={isMobile}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                    },
                }}>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, paddingTop: '29px !important' }}>
                    <Box sx={{
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 2
                    }}>
                        <CloseIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" align="center" sx={{ color: '#333', fontWeight: 'bold' }}>
                        ¿Está seguro de que desea eliminar esta ruta?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'right',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button onClick={confirmDeleteRoute}
                        variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgb(255, 0, 0)',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(255, 0, 0, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}>
                        Eliminar
                    </Button>
                    <Button onClick={() => setDeleteDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                            mr: 2,
                        }}
                    >
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteRouteUnionDialogOpen}
                onClose={() => setDeleteRouteUnionDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '8px' }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 4,
                    paddingTop: '29px !important'
                }}>
                    <Box sx={{
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 2
                    }}>
                        <CloseIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" align="center" sx={{ color: '#333', fontWeight: 'bold' }}>
                        ¿Está seguro de que desea eliminar esta ruta?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'right',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button
                        onClick={handleDeleteRouteUnion}
                        variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgb(255, 0, 0)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}
                    >
                        Eliminar
                    </Button>
                    <Button
                        onClick={() => setDeleteRouteUnionDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                            mr: 2,
                        }}
                    >
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Custom Notification Popup */}
            <NotificationPopupReprogram />
        </CalendarContainer>
    );
};

export default React.memo(OrdersCalendar);
